import React, { useEffect, useRef, useState } from "react";
import { addDays, getDay } from "date-fns";
import dayjs, { Dayjs } from "dayjs";
// Removed duplicate import of DatePicker
import AppButton from "../../UI/AppButtons";
import AppCard from "../../UI/AppCards";
import { AppInputSwitcher } from "../AppInputSwitcher/AppInputSwitcher";
import { AppRadioButton } from "../../UI/AppRadioButtons/AppRadioButtons";
import { AppStandardMultiSelectCheckBox } from "../../UI/AppMultipleSelection/AppMultiSelection";
import BookingApi from "../../../API/Apis/BookingApi";
import {
  AffiliateDetails,
  BookingDetails,
} from "../../../@types/Booking/bookingDetails";
import { BookingFormProps } from "./BookingForm.props";
import BookingGetters from "../../../store/onlineBooking/getters";
import {
  Location,
  LocationOpening,
  Affiliate,
} from "../../../@types/DTO/Locations";
import SearchIcon from "@mui/icons-material/Search";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import TrainIcon from "@mui/icons-material/Train";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import { setTime } from "../../../helpers/dateManipulators";
import { useTranslation } from "react-i18next";
import { BookingSourceResponse } from "../../../@types/DTO/BookingSource";
import { t } from "i18next";
import { MacroClassGroup } from "../../../@types/DTO/MacroClass";
import { useSearchParams } from "react-router-dom";
import { formatTheme } from "../../../config/MuiColorThemes";
import { createTheme, Theme, ThemeProvider } from "@mui/material/styles";
import TagManager from "react-gtm-module";
import i18n from "../../../config/i18n";
import { BOOKING_ACTIONS } from "../../../store/onlineBooking";
import { useDispatch } from "react-redux";
import { DatePickerComponent } from "../../UI/AppDatePickers/AppDatePicker";
import { CircularProgress } from "@mui/material";

function getEnabledTimeSlots(
  locationArray: Location[],
  locationName: string,
  date: Date | null
) {
  const AppSettings = BookingGetters.getAppSettings();
  const currentTime = new Date();

  const formatDate = (date) => {
    const d = new Date(date);
    d.setHours(currentTime.getHours());
    d.setMinutes(currentTime.getMinutes());
    d.setSeconds(currentTime.getSeconds());
    d.setMilliseconds(currentTime.getMilliseconds());
    return d.toISOString().split("T")[0];
  };

  const locationLang = i18n.language;

  return locationName && date
    ? locationArray
        .filter((el) => {
          const locationValue = AppSettings?.showLocationWebDescription
            ? locationLang === "it" && el.publicWebDescription
              ? el.publicWebDescription
              : locationLang === "en" &&
                el.publicWebDescriptionEN &&
                AppSettings?.showLocationWebDescription
              ? el.publicWebDescriptionEN
              : el.locationName
            : el.locationCode +
              "-" +
              el.locationName +
              " " +
              el.locationNumber +
              " " +
              el.locationAddress +
              " " +
              el.locationCity +
              " " +
              el.zipCode;
          return locationValue.toLowerCase() === locationName.toLowerCase();
        })
        .flatMap((el) => {
          if (el?.isOutOfHours) {
            return [
              {
                start: setTime("00:00", date),
                end: setTime("23:59", date),
              },
            ];
          }

          return el.openings
            .filter((e) => {
              const normalizeDate = (d) =>
                new Date(d.toISOString().split("T")[0]);
              let selectedDate = new Date(date);
              let isInValidPeriod = true;
              if (e?.validFrom != null && e?.validTo != null) {
                selectedDate = normalizeDate(selectedDate);
                const validFrom = new Date(e?.validFrom);
                const validTo = new Date(e?.validTo);
                isInValidPeriod =
                  selectedDate >= validFrom && selectedDate <= validTo;
              }
              if (!isInValidPeriod) {
                isInValidPeriod = el.isOutOfHours || isInValidPeriod;
              }

              return (
                isInValidPeriod &&
                (e.dayOfTheWeek % 7 == getDay(date) ||
                  (e.specificDay && e.specificDay == formatDate(date)))
              );
            })
            .map((e) => {
              return {
                start: setTime(e.startTime, date),
                end: setTime(e.endTime, date),
              };
            });
        })
    : [];
}

function getEnabledDropoffTimeSlots(
  locationArray: Location[],
  locationName: string,
  date: Date | null
) {
  const AppSettings = BookingGetters.getAppSettings();
  const currentTime = new Date();
  const formatDate = (date) => {
    const d = new Date(date);
    d.setHours(currentTime.getHours());
    d.setMinutes(currentTime.getMinutes());
    d.setSeconds(currentTime.getSeconds());
    d.setMilliseconds(currentTime.getMilliseconds());
    return d.toISOString().split("T")[0];
  };
  const locationLang = i18n.language;

  if (!locationName || !date) return [];
  const location = locationArray.find((el) => {
    const locationValue = AppSettings?.showLocationWebDescription
      ? locationLang === "it" && el.publicWebDescription
        ? el.publicWebDescription
        : locationLang === "en" &&
          el.publicWebDescriptionEN &&
          AppSettings?.showLocationWebDescription
        ? el.publicWebDescriptionEN
        : el.locationName
      : el.locationCode +
        "-" +
        el.locationName +
        " " +
        el.locationNumber +
        " " +
        el.locationAddress +
        " " +
        el.locationCity +
        " " +
        el.zipCode;
    return locationValue.toLowerCase() === locationName.toLowerCase();
  });

  // If onlyDropOffOutOfHours is true, enable all times
  if (location?.onlyDropOffOutOfHours || location?.isOutOfHours) {
    return [
      {
        start: setTime("00:00", date),
        end: setTime("23:59", date),
      },
    ];
  }

  // Existing logic for restricted times
  return location
    ? location.openings
        .filter((e) => {
          const normalizeDate = (d) => new Date(d.toISOString().split("T")[0]);
          let selectedDate = new Date(date);
          let isInValidPeriod = true;
          selectedDate = normalizeDate(selectedDate);
          if (e?.validFrom != null && e?.validTo != null) {
            const validFrom = new Date(e?.validFrom);
            const validTo = new Date(e?.validTo);
            isInValidPeriod =
              selectedDate >= validFrom && selectedDate <= validTo;
          }

          if (!isInValidPeriod) {
            isInValidPeriod = location.onlyDropOffOutOfHours || isInValidPeriod;
          }
          return (
            isInValidPeriod &&
            (e.dayOfTheWeek % 7 == getDay(date) ||
              (e.specificDay && e.specificDay == formatDate(date)))
          );
        })
        .map((e) => {
          return {
            start: setTime(e.dropoffStartTime, date),
            end: setTime(e.dropoffendTime, date),
          };
        })
    : [];
}

// function getEnabledDropoffTimeSlots(
//   locationArray: Location[],
//   locationName: string,
//   date: Date | null
// ) {
//   const AppSettings = BookingGetters.getAppSettings();
//   const currentTime = new Date();
//   const formatDate = (date) => {
//     const d = new Date(date);
//     d.setHours(currentTime.getHours());
//     d.setMinutes(currentTime.getMinutes());
//     d.setSeconds(currentTime.getSeconds());
//     d.setMilliseconds(currentTime.getMilliseconds());
//     return d.toISOString().split("T")[0];
//   };
//   const locationLang = i18n.language;
//   return locationName && date
//     ? locationArray
//         .filter((el) => {
//           const locationValue = AppSettings?.showLocationWebDescription
//             ? locationLang === "it" && el.publicWebDescription
//               ? el.publicWebDescription
//               : locationLang === "en" &&
//                 el.publicWebDescriptionEN &&
//                 AppSettings?.showLocationWebDescription
//               ? el.publicWebDescriptionEN
//               : el.locationName
//             : el.locationCode +
//               "-" +
//               el.locationName +
//               " " +
//               el.locationNumber +
//               " " +
//               el.locationAddress +
//               " " +
//               el.locationCity +
//               " " +
//               el.zipCode;
//           return locationValue.toLowerCase() === locationName.toLowerCase();
//         })
//         .flatMap((el) => {
//           return el.openings
//             .filter((e) => {
//               const normalizeDate = (d) =>
//                 new Date(d.toISOString().split("T")[0]);
//               let selectedDate = new Date(date);
//               let isInValidPeriod = true;
//               selectedDate = normalizeDate(selectedDate);
//               if (e?.validFrom != null && e?.validTo != null) {
//                 const validFrom = new Date(e?.validFrom);
//                 const validTo = new Date(e?.validTo);
//                 isInValidPeriod =
//                   selectedDate >= validFrom && selectedDate <= validTo;
//               }
//
//               if (!isInValidPeriod) {
//                 isInValidPeriod = el.onlyDropOffOutOfHours || isInValidPeriod;
//               }
//               return (
//                 isInValidPeriod &&
//                 (e.dayOfTheWeek % 7 == getDay(date) ||
//                   (e.specificDay && e.specificDay == formatDate(date)))
//               );
//             })
//             .map((e) => {
//               return {
//                 start: setTime(e.dropoffStartTime, date),
//                 end: setTime(e.dropoffendTime, date),
//               };
//             });
//         })
//     : [];
// }

function BookingForm({
  onSubmit = () => {},
  className = "",
  isEnableHorizontalView,
}: BookingFormProps): JSX.Element {
  const [dateError, setDateError] = useState("");
  const locationLang = i18n.language;
  const dynamicPalette = formatTheme();
  const dynamicTheme = createTheme(dynamicPalette);
  const { t: translate } = useTranslation();
  const reduxBookingDetails: BookingDetails =
    BookingGetters.getBookingDetails();
  const AppSettings = BookingGetters.getAppSettings();
  const maxRentalAllowedDay = AppSettings?.maxRentalDurationInDays || 30;
  const [minRentalAllowedDay] = useState<number>(AppSettings?.minRentDays || 0);
  const [pickupLocation, setPickupLocation] = useState<string>("");
  const [reservationDestination, setReservationDestination] =
    useState<string>("");
  const [macroGroupOptions, setMacroGroupOptions] = useState<MacroClassGroup[]>(
    []
  );
  const [ageCriteriaData, setAgeCriteriaData] = useState([]);
  const [selectedMacroGroup, setSelectedMacroGroup] = useState<string>("");
  const [selectedMacroGroupWebCode, setSelectedMacroGroupWebCode] =
    useState<string>("");
  const [reservationStartDate, setStartDate] = useState<Date | null>(
    reduxBookingDetails.pickUpDateAndTime
      ? new Date(reduxBookingDetails.pickUpDateAndTime)
      : null
  );

  const [reservationEndDate, setEndDate] = useState<Date | null>(
    reduxBookingDetails.dropOffDateAndTime
      ? new Date(reduxBookingDetails.dropOffDateAndTime)
      : null
  );
  const [reservationPlatform, setReservationPlatform] = useState<string>(
    reduxBookingDetails.reservationSource
      ? reduxBookingDetails.reservationSource.name
      : ""
  );
  const [isDroppingAtSameLocation, setIsDroppingAtSameLocation] =
    useState<boolean>(
      reduxBookingDetails.dropAtSameLocation == null ||
        reduxBookingDetails.dropAtSameLocation == true
    );
  const [reservationSourceOptions, setReservationSourceOptions] = useState<
    BookingSourceResponse[]
  >([]);
  const [reseSourceMacroOptions, setReseSourceMacroOptions] = useState<
    BookingSourceResponse[]
  >([]);
  const [isYoungDriverAge, setIsYoungDriverAge] = useState<string>(
    reduxBookingDetails.isYoungDriverAge
      ? reduxBookingDetails.isYoungDriverAge
      : "false"
  );
  const [isSeniorDriverAge, setIsSeniorDriverAge] = useState<string>(
    reduxBookingDetails.isSeniorDriverAge
      ? reduxBookingDetails.isSeniorDriverAge
      : "false"
  );
  const [isNoFeeAge, setIsNoFeeAge] = useState<string>(
    reduxBookingDetails.noFeeAge ? reduxBookingDetails.noFeeAge : "false"
  );
  const [youngDriver2, setYoungDriver2] = useState<string>(
    reduxBookingDetails.isYoungDriver2Age
      ? reduxBookingDetails.isYoungDriver2Age
      : "false"
  );
  const [isAgeLimitSelected, setIsAgeLimitSelected] = useState<string>(
    reduxBookingDetails.isAgeLimitSelected
  );

  const [queryParams] = useSearchParams();
  const scriptVoucherCode = queryParams.get("scriptVoucherCode");
  const [agreementCoupon, setagreementCoupon] = useState<string>(
    scriptVoucherCode
      ? scriptVoucherCode
      : reduxBookingDetails.agreementCoupon || ""
  );
  const [disableCoupon, setDisableCoupon] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");
  const [discountExpireText, setDiscountExpireText] = useState<String>("");
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    if (!scriptVoucherCode) return;

    const checkInitialCoupon = async () => {
      setIsModalLoading(true);
      try {
        const channelRes = await BookingApi.getVoucherWithScript(
          scriptVoucherCode
        );

        const isChannelFound = channelRes?.data?.data?.isChannelFound;

        if (isChannelFound === "Y") {
          setDisableCoupon(true);
        } else {
          setDisableCoupon(false);
        }

        const tempStart = reservationStartDate
          ? new Date(reservationStartDate).toISOString()
          : new Date().toISOString();

        const tempEnd = reservationEndDate
          ? new Date(reservationEndDate).toISOString()
          : new Date().toISOString();

        const response = await BookingApi.couponDiscount(
          scriptVoucherCode.trim(),
          tempStart,
          tempEnd,
          0,
          0,
          0,
          0
        );

        const status = response?.data?.data?.bookingStatus;
        const expiredCoupon = response?.data?.data?.msg;

        if (status === "success") {
          setShowModal(false);
        } else if (expiredCoupon === "Agreement coupon is not valid") {
          setModalMessage(translate("attention_discount_expire"));
          setShowModal(true);
        } else {
          setModalMessage(translate("discountNotValidText"));
          setShowModal(true);
        }
      } catch (error) {
        console.error("Coupon validation error:", error);
      } finally {
        setIsModalLoading(false);
      }
    };

    checkInitialCoupon();
  }, [scriptVoucherCode]);

  const [discountValueWithoutVat] = useState<any>(
    reduxBookingDetails.discountValueWithoutVat
      ? reduxBookingDetails.discountValueWithoutVat
      : "0"
  );

  const [locationDetail, setLocationDetail] = useState<Location[]>([]);
  const [locationDropOffDetail, setDropOffLocationDetail] = useState<
    Location[]
  >([]);
  const [codiceLabel, setCodiceLabel] = useState<String>("");
  const [pageTitleLabel, setPageTitleLabel] = useState<String>("");
  const [searchText, setSearchText] = useState<String>("");
  const [macroGroupLabel, setMacroGroupLabel] = useState("");
  const [bookingReservationSource, setbookingReservationSource] = useState("");
  const [bookingResSourceForReservedArea, setBookingResSourceForReservedArea] =
    useState("");
  const [reservationSourceEnabled, setReservationSourceEnabled] =
    useState(false);
  const [reservationSourceForUser, setReservationSourceForUser] = useState<
    BookingSourceResponse[]
  >([]);
  const [resSourceMacroForUser, setResSourceMacroForUser] = useState<
    BookingSourceResponse[]
  >([]);
  const mountedRef = useRef(false);
  const userId: any = localStorage.getItem("userID");
  const isUserValid = userId && userId !== "null" && userId !== "";
  const [reservedAreaPayNow, setReservedAreaPayNow] = useState(false);
  useEffect(() => {
    const fetchResSources = async () => {
      if (!userId || userId == "null") return;
      setReservationSourceEnabled(true);
      const e = await BookingApi.getReservationSourceForOnlineUser(userId);
      const resList = e?.data?.reservationSourceList ?? [];
      const sortedSource = resList
        .filter((item) => item && item.id !== undefined)
        .sort((a, b) => {
          const labelA = (
            a["Ragione Sociale textRS"] ||
            a["Ragione Sociale"] ||
            ""
          ).trim();
          const labelB = (
            b["Ragione Sociale textRS"] ||
            b["Ragione Sociale"] ||
            ""
          ).trim();

          const startsWithNumA = /^\d/.test(labelA);
          const startsWithNumB = /^\d/.test(labelB);

          if (startsWithNumA && startsWithNumB) {
            const numA = parseInt(labelA.match(/^\d+/)[0], 10);
            const numB = parseInt(labelB.match(/^\d+/)[0], 10);

            if (numA !== numB) {
              return numA - numB;
            }
            return labelA.localeCompare(labelB);
          }
          if (startsWithNumA) return -1;
          if (startsWithNumB) return 1;

          return labelA.localeCompare(labelB);
        });
      const resSourceSorted = resList.length > 0 ? sortedSource : [];
      setReservationSourceForUser(resSourceSorted); //
      setResSourceMacroForUser(resSourceSorted);
      const defaultSource =
        resSourceSorted.find((item) => item.isDefaultEnable) ??
        resSourceSorted[0];
      setReservationPlatform(
        defaultSource?.["Ragione Sociale textRS"] ?? defaultSource ?? ""
      );
      mountedRef.current = true;

      if (
        AppSettings?.enablePerDayReservationSource &&
        reservationStartDate &&
        reservationEndDate &&
        AppSettings?.showReservationGroupSelection
      ) {
        const formattedStartDate =
          formatDateInItalianTime(reservationStartDate);
        const formattedEndDate = formatDateInItalianTime(reservationEndDate);

        const start_date_time = reservationStartDate?.getTime() || Date.now();
        const end_date_time =
          reservationEndDate?.getTime() ||
          start_date_time + 24 * 60 * 60 * 1000;
        const startTime = formatTimeInItalianTime(new Date(start_date_time));
        const endTime = formatTimeInItalianTime(new Date(end_date_time));
        const dateResSources =
          await BookingApi.getReservationSourcesOnStartAndEndDate(
            formattedStartDate,
            formattedEndDate,
            locationLang,
            startTime,
            endTime
          );

        const data = dateResSources?.data ?? [];
        if (data.length > 0) {
          let commonReservationSources = data.filter((d: { Codice: string }) =>
            resList.some((u: { code: string }) => u.code === d.Codice)
          );
          if (commonReservationSources.length === 0) {
            commonReservationSources = resList;
          }
          const sortedResSource = commonReservationSources
            .filter((item) => item && item.id !== undefined)
            .sort((a, b) => {
              const labelA = (
                a["Ragione Sociale textRS"] ||
                a["Ragione Sociale"] ||
                ""
              ).trim();
              const labelB = (
                b["Ragione Sociale textRS"] ||
                b["Ragione Sociale"] ||
                ""
              ).trim();

              const startsWithNumA = /^\d/.test(labelA);
              const startsWithNumB = /^\d/.test(labelB);

              if (startsWithNumA && startsWithNumB) {
                const numA = parseInt(labelA.match(/^\d+/)[0], 10);
                const numB = parseInt(labelB.match(/^\d+/)[0], 10);

                if (numA !== numB) {
                  return numA - numB;
                }
                return labelA.localeCompare(labelB);
              }
              if (startsWithNumA) return -1;
              if (startsWithNumB) return 1;

              return labelA.localeCompare(labelB);
            });
          setReservationSourceForUser(sortedResSource);
          setResSourceMacroForUser(sortedResSource);
          const defaultSource =
            sortedResSource.find((item) => item.isDefaultEnable) ??
            sortedResSource[0];
          // console.log("defaultSource", defaultSource);
          setReservationPlatform(
            defaultSource?.["Ragione Sociale textRS"] ?? defaultSource ?? ""
          );
        }
      }
    };
    fetchResSources().catch(console.error);
  }, [userId, reservationStartDate, reservationEndDate]);

  useEffect(() => {
    const isLoggedInUser = userId != null && userId != "null" && userId != "";
    if (reservationPlatform && reservationSourceForUser && isLoggedInUser) {
      const selectedObj = reservationSourceForUser.find(
        (item) =>
          item.description === reservationPlatform ||
          item["Ragione Sociale textRS"] === reservationPlatform
      );
      setReservedAreaPayNow(selectedObj?.payNow ?? false);
    } else if (
      reservationPlatform &&
      !isLoggedInUser &&
      reservationSourceOptions
    ) {
      const selectedObj = reservationSourceOptions.find(
        (item) =>
          item["Ragione Sociale"] === reservationPlatform ||
          item["Ragione Sociale textRS"] === reservationPlatform
      );
      setReservedAreaPayNow(selectedObj?.payNow ?? false);
    }
  }, [reservationPlatform, reservationSourceForUser, reservationSourceOptions]);
  const [leadTimeAlertMsg, setLeadTimeAlertMsg] = useState<any>("");
  const [pickUpDate, setPickUpDate] = useState<String>("");
  const [dropOffDate, setDropOffDate] = useState<String>("");
  const [pickUpTime, setPickUpTime] = useState<String>("");
  const [dropOffTime, setDropOffTime] = useState<String>("");
  const [
    showTextBeginningOfBookingFormMsg,
    setShowTextBeginningOfBookingFormMsg,
  ] = useState<String>("");
  const textResourceDetails =
    BookingGetters.getTextResourceDetailsBasedOnLang();
  const data =
    textResourceDetails?.[locationLang]?.flatMap((item) => item.data || []) ||
    [];
  const [discountNotValidText, setDiscountNotValidText] = useState<String>("");
  const isDesktopScreen = window.innerWidth >= 1310;
  useEffect(() => {
    const bookingSearchTextData = data.find(
      (item) => item.key === "bookingSearchLabel"
    );
    const bookingReservationSourceData = data.find(
      (item) => item.key === "bookingReservationSource"
    );
    const bookingResSourceForReservedAreaData = data.find(
      (item) => item.key === "bookingResSourceForReservedArea"
    );
    const codiceVoucherLabel = data.find(
      (item) => item.key === "codiceVoucherLabel"
    );
    const TitleAtFirstPageLabel = data.find(
      (item) => item.key === "TitleAtFirstPageLabel"
    );
    const macroGroupLabelData = data.find(
      (item) => item.key === "macroGroupLabel"
    );
    const leadTimeAlertMsgData = data.find(
      (item) => item.key === "leadTimeAlertMessage"
    );
    const showTextBeginningOfBookingFormData = data.find(
      (item) => item.key === "showTextBeginningOfBookingFormLable"
    );
    setMacroGroupLabel(
      macroGroupLabelData ? macroGroupLabelData.body : translate("Macro Group")
    );
    setCodiceLabel(codiceVoucherLabel?.body || translate("Voucher Code"));
    setPageTitleLabel(TitleAtFirstPageLabel?.body || t("Movee Car Rental"));
    setSearchText(
      bookingSearchTextData
        ? bookingSearchTextData?.body
        : translate("Find vehicles")
    );
    setLeadTimeAlertMsg(
      leadTimeAlertMsgData?.body ||
        translate("You can start booking after {timeValue}")
    );
    setShowTextBeginningOfBookingFormMsg(
      showTextBeginningOfBookingFormData?.body ||
        translate("show_text_beginning_of_booking_form")
    );
    setbookingReservationSource(
      bookingReservationSourceData
        ? bookingReservationSourceData.body
        : translate("reservation source")
    );
    setBookingResSourceForReservedArea(
      bookingResSourceForReservedAreaData
        ? bookingResSourceForReservedAreaData.body
        : translate("reservation source")
    );

    const pickUpTextData = data.find((item) => item.key === "pickUpDate");
    setPickUpDate(pickUpTextData?.body || t("Pick up date"));
    const dropOffTextData = data.find((item) => item.key === "dropOffDate");
    setDropOffDate(dropOffTextData?.body || t("Drop off date"));
    const pickUpTimeTextData = data.find((item) => item.key === "pickUpTime");
    setPickUpTime(pickUpTimeTextData?.body || translate("pick up time"));
    const dropOffTimeTextData = data.find((item) => item.key === "dropOffTime");
    setDropOffTime(dropOffTimeTextData?.body || translate("drop off time"));

    const discountExpireTextData = data.find(
      (item) => item.key === "discountExpireText"
    );
    setDiscountExpireText(
      discountExpireTextData?.body || translate("attention_discount_expire")
    );

    const discountNotValidTextData = data.find(
      (item) => item.key === "discountNotValidText"
    );
    setDiscountNotValidText(
      discountNotValidTextData?.body || translate("discountNotValidText")
    );
  }, [data, locationLang, translate]);
  function requestParentResize() {
    const iframeMobile = AppSettings?.iframeHeightFirstPageForMobile;
    const iframeDesktop = AppSettings?.iframeHeightForFirstPage;
    const iframeHeight = isDesktopScreen
      ? iframeDesktop || document.body.scrollHeight
      : iframeMobile || document.body.scrollHeight + 100;
    window.parent.postMessage(
      {
        event_id: "iframeHeight",
        data: {
          iframeHeight: iframeHeight,
        },
      },
      "*"
    );
  }
  const [tagManagerId, setTagManagerId] = useState("");
  const [isTagManagerEnabled, setIsTagManagerEnabled] = useState(false);
  const [isAffiliateEnable, setIsAffiliateEnable] = useState<boolean>(false);
  const [smallScreen] = useState(window.innerWidth <= 768);
  const istabletScreen = window.innerWidth > 768 && window.innerWidth < 1300;
  const [filterViaMacro, setFilterViaMacro] = useState(false);

  useEffect(() => {
    setIsTagManagerEnabled(!!AppSettings?.enableTagManager);
    setTagManagerId(AppSettings?.tagManagerId || "");
    setIsAffiliateEnable(AppSettings?.enableAffiliate);
    setFilterViaMacro(AppSettings?.fonteViaMacroClass);
  }, [AppSettings]);
  useEffect(() => {
    if (isTagManagerEnabled && tagManagerId != "") {
      const tagManagerArgs = {
        gtmId: tagManagerId,
      };
      TagManager.initialize(tagManagerArgs); // google tag manager integrated along with the preferences
    }
  }, [tagManagerId, isTagManagerEnabled]);

  useEffect(() => {
    if (AppSettings?.showInIframe) {
      requestParentResize();
    }
  }, []);
  const [oldResStartDate, setOldResStartDate] = useState<Date | null>();
  const [oldResEndDate, setOldResEndDate] = useState<Date | null>();
  const currentTime = new Date();
  const formatDate = (date) => {
    const d = new Date(date);
    d.setHours(currentTime.getHours());
    d.setMinutes(currentTime.getMinutes());
    d.setSeconds(currentTime.getSeconds());
    d.setMilliseconds(currentTime.getMilliseconds());
    return d.toISOString().split("T")[0];
  };
  useEffect(() => {
    const openingSchedule: LocationOpening[] = [];
    const resStartDate =
      reservationStartDate != null ? new Date(reservationStartDate) : null;
    const resEndDate =
      reservationEndDate != null ? new Date(reservationEndDate) : null;
    if (resStartDate != null && resEndDate != null) {
      resStartDate.setHours(0, 0, 0, 0);
      resEndDate.setHours(0, 0, 0, 0);
      if (
        resStartDate.getTime() != oldResStartDate?.getTime() ||
        resEndDate.getTime() != oldResEndDate?.getTime()
      ) {
        setOldResStartDate(resStartDate);
        setOldResEndDate(resEndDate);
        for (const loc of locationDetail) {
          if (
            loc.locationName === reservationDestination ||
            loc.publicWebDescription == reservationDestination ||
            loc.publicWebDescriptionEN == reservationDestination
          ) {
            loc.openings.forEach((time) => {
              if (
                time.dayOfTheWeek % 7 == reservationEndDate?.getDay() ||
                (time.specificDay &&
                  time.specificDay == formatDate(reservationStartDate))
              ) {
                openingSchedule.push(time);
              }
            });
            break;
          }
        }
        for (const openings of openingSchedule) {
          if (
            openings?.isValidPeriod == "false" ||
            openings?.isValidPeriod == "null"
          ) {
            let dropOffTime;
            if (resStartDate.getTime() === resEndDate.getTime()) {
              dropOffTime =
                openings?.LocationStartTime || openings?.dropoffendTime;
            } else {
              dropOffTime =
                openings?.LocationStartTime || openings?.dropoffStartTime;
            }
            setEndDate(setTime(dropOffTime, reservationEndDate || new Date()));
            if (openings?.startTime > reservationStartDate?.getTime()) {
              setStartDate(
                setTime(
                  openings?.LocationStartTime || openings?.startTime,
                  reservationStartDate || new Date()
                )
              );
            }
            // setStartDate(
            //   setTime(
            //     openings?.LocationStartTime || openings?.startTime,
            //     reservationStartDate || new Date()
            //   )
            // );
            break;
          } else if (
            openings?.validTo != null &&
            openings?.validFrom != null &&
            reservationEndDate &&
            reservationEndDate?.getTime() >
              new Date(openings?.validFrom).getTime() &&
            reservationEndDate?.getTime() <
              new Date(openings?.validTo).getTime()
          ) {
            let dropOffTime;
            if (resStartDate.getTime() === resEndDate.getTime()) {
              dropOffTime =
                openings?.LocationStartTime || openings?.dropoffendTime;
            } else {
              dropOffTime =
                openings?.LocationStartTime || openings?.dropoffStartTime;
            }
            setEndDate(setTime(dropOffTime, reservationEndDate || new Date()));
            if (openings?.startTime > reservationStartDate?.getTime()) {
              setStartDate(
                setTime(
                  openings?.LocationStartTime || openings?.startTime,
                  reservationStartDate || new Date()
                )
              );
            }
            // setStartDate(
            //   setTime(
            //     openings?.LocationStartTime || openings?.startTime,
            //     reservationStartDate || new Date()
            //   )
            // );
            break;
          }
        }
        const currentDateWithoutTime = new Date();
        currentDateWithoutTime.setHours(0, 0, 0, 0);
        if (resStartDate.getTime() === currentDateWithoutTime.getTime()) {
          const currentDateWithTime = new Date();
          let hour = currentDateWithTime.getHours();
          const minutes = currentDateWithTime.getMinutes() < 30 ? 30 : 0;
          hour += currentDateWithTime.getMinutes() < 30 ? 0 : 1;
          setStartDate(
            setTime(hour + ":" + minutes, reservationStartDate || new Date())
          );
          if (resStartDate.getTime() == resEndDate.getTime()) {
            setEndDate(setTime(hour + 1 + ":" + 0, resEndDate || new Date()));
          }
        }
      }
    }
  }, [reservationStartDate, reservationEndDate]);

  useEffect(() => {
    BookingApi.getBookingAgeCriteria(locationLang).then((response) => {
      const data = response?.data;
      if (data) {
        Object.keys(data).forEach((key) => {
          if (
            key === "youngDriver" ||
            key === "noAgeDriver" ||
            key === "seniorDriver" ||
            key === "youngDriver2"
          ) {
            setAgeCriteriaData((prevState) => ({
              ...prevState,
              [key]: data[key],
            }));
          }
        });
        if (data.selectYoungDriver) {
          setIsAgeLimitSelected("youngDriver");
        } else if (data.selectYoungDriver2) {
          setIsAgeLimitSelected("youngDriver2");
        } else if (data.selectNoAgeDriver) {
          setIsAgeLimitSelected("noAgeFeeDriver");
        } else if (data.selectSeniorDriver) {
          setIsAgeLimitSelected("seniorDriver");
        }
      }
    });
  }, []);

  useEffect(() => {
    const affiliateDetails = reduxBookingDetails.affiliateDetails;
    const macroClassDetails = reduxBookingDetails.macroGroupName;
    const resSourceDetails = reduxBookingDetails.reservationSource.name;
    if (affiliateDetails) {
      setSelectedAffiliateName(affiliateDetails.name);
      setSelectedAffiliate(affiliateDetails.code);
    } else {
      setSelectedAffiliateName("");
    }
    if (macroClassDetails && userId == null) {
      setSelectedMacroGroup(macroClassDetails);
      setSelectedMacroGroupWebCode(macroClassDetails);
    }
    if (resSourceDetails && userId == null) {
      setReservationPlatform(resSourceDetails);
    }
    if (AppSettings?.showLocationWebDescription) {
      const locationDetails = reduxBookingDetails.pickUpLocationDetails;
      if (locationDetails) {
        const description =
          locationLang === "it"
            ? locationDetails.publicWebDescription || locationDetails.name
            : locationLang === "en"
            ? locationDetails.publicWebDescriptionEN || locationDetails.name
            : "";

        setPickupLocation(description);
      } else {
        setPickupLocation("");
      }
    } else {
      setPickupLocation(
        reduxBookingDetails.pickUpLocationDetails.name
          ? reduxBookingDetails.pickUpLocationDetails.code +
              "-" +
              reduxBookingDetails.pickUpLocationDetails.name +
              " " +
              reduxBookingDetails.pickUpLocationDetails.locationNumber +
              " " +
              reduxBookingDetails.pickUpLocationDetails.locationAddress +
              " " +
              reduxBookingDetails.pickUpLocationDetails.locationCity +
              " " +
              reduxBookingDetails.pickUpLocationDetails.zipCode
          : ""
      );
    }
    if (AppSettings?.showLocationWebDescription) {
      const locationDetails = reduxBookingDetails.dropOffLocationDetails;

      if (locationDetails) {
        const description =
          locationLang === "it"
            ? locationDetails.publicWebDescription || locationDetails.name
            : locationLang === "en"
            ? locationDetails.publicWebDescriptionEN || locationDetails.name
            : "";

        setReservationDestination(description);
      } else {
        setReservationDestination("");
      }
    } else {
      setReservationDestination(
        reduxBookingDetails.dropOffLocationDetails.name
          ? reduxBookingDetails.dropOffLocationDetails.code +
              "-" +
              reduxBookingDetails.dropOffLocationDetails.name +
              " " +
              reduxBookingDetails.dropOffLocationDetails.locationNumber +
              " " +
              reduxBookingDetails.dropOffLocationDetails.locationAddress +
              " " +
              reduxBookingDetails.dropOffLocationDetails.locationCity +
              " " +
              reduxBookingDetails.dropOffLocationDetails.zipCode
          : ""
      );
    }
  }, [AppSettings, reduxBookingDetails, locationLang]);
  useEffect(() => {
    if (isAgeLimitSelected === "youngDriver") {
      setIsYoungDriverAge("true");
      setIsSeniorDriverAge("false");
      setIsNoFeeAge("false");
      setYoungDriver2("false");
    } else if (
      isAgeLimitSelected === "noAgeFeeDriver" ||
      isAgeLimitSelected === "noAgeDriver"
    ) {
      setIsYoungDriverAge("false");
      setIsSeniorDriverAge("false");
      setIsNoFeeAge("true");
      setYoungDriver2("false");
    } else if (isAgeLimitSelected === "seniorDriver") {
      setIsYoungDriverAge("false");
      setIsSeniorDriverAge("true");
      setIsNoFeeAge("false");
      setYoungDriver2("false");
    } else if (isAgeLimitSelected === "youngDriver2") {
      setIsYoungDriverAge("false");
      setIsSeniorDriverAge("false");
      setIsNoFeeAge("false");
      setYoungDriver2("true");
    } else {
      setIsYoungDriverAge("false");
      setIsSeniorDriverAge("false");
      setIsNoFeeAge("false");
      setYoungDriver2("false");
    }
  }, [isAgeLimitSelected]);
  const userAffiliate = localStorage.getItem("userAffiliate");
  const [selectedAffiliate, setSelectedAffiliate] = useState<string>();
  const [selectedAffiliateName, setSelectedAffiliateName] =
    useState<string>("");

  const scriptAffiliate = queryParams.get("scriptAffiliate");
  const [affiliateDetail, setAffiliateDetail] = useState<Affiliate[]>([]);
  const affiliateOptions = affiliateDetail.map((e) => {
    const optionValue = e[1];
    const icon = <LocationCityIcon htmlColor="#1860ab" />;
    return {
      label: optionValue,
      value: optionValue,
      icon,
    };
  });
  const scriptAffiliateIndex = Number(scriptAffiliate);
  useEffect(() => {
    if (isAffiliateEnable) {
      BookingApi.getAffiliate().then((data: Record<string, any>) => {
        const userId: any = localStorage.getItem("userID");
        let attachedOnlineAffiliates = data.data.affiliates;
        // Exclude specific affiliates
        attachedOnlineAffiliates = attachedOnlineAffiliates.filter(
          (affiliate: any) =>
            affiliate[1] !== "Dogma Systems" &&
            affiliate[1] !== "WORLD APP. SRL"
        );
        // If scriptAffiliate parameter exists, filter by affiliateId
        if (
          scriptAffiliate !== null &&
          !isNaN(scriptAffiliateIndex) &&
          scriptAffiliate != ""
        ) {
          const affiliateIdToFind = Number(scriptAffiliate);
          const selectedAffiliate = attachedOnlineAffiliates.find(
            (affiliate: any) => affiliate[0] === affiliateIdToFind
          );
          if (selectedAffiliate && selectedAffiliate !== "undefined") {
            sessionStorage.setItem("scriptAffiliate", scriptAffiliate);
          }
          if (selectedAffiliate) {
            setAffiliateDetail([selectedAffiliate]);
            setSelectedAffiliate(selectedAffiliate[0]);
            setSelectedAffiliateName(selectedAffiliate[1]);
            return;
          }
        }

        if (
          userId != null &&
          userId != "null" &&
          userId != "" &&
          userAffiliate != null
        ) {
          const filteredAffiliates = attachedOnlineAffiliates.filter(
            (affiliate: any) => affiliate[0] == userAffiliate
          );
          setSelectedAffiliate(userAffiliate);
          setSelectedAffiliateName(filteredAffiliates[0][1]);
        } else {
          setAffiliateDetail(attachedOnlineAffiliates || []);
        }
      });
    }
  }, [isAffiliateEnable]);
  const [isInvalidScriptChannelResSource, setIsInvalidScriptChannelResSource] =
    useState<boolean>(false);
  const [isScriptChannelResSource, setIsScriptChannelResSource] =
    useState<boolean>(false);
  const scriptChannel = queryParams.get("scriptChannel");
  const locInfo = BookingGetters.getAffiliateLocationDetails();
  const dispatch = useDispatch();
  const fetchLocationApi = async (affiliateId: string) => {
    try {
      const data: Record<string, any> =
        await BookingApi.getReservationLocations(affiliateId);
      const locationError = data.data.status == "error";
      if (locationError) {
        setLocationDetail([]);
        return;
      }
      let sortByLocationName;
      if (AppSettings?.enableLocationInReverseOrder) {
        sortByLocationName = (a, b) => {
          return b.locationName.localeCompare(a.locationName);
        };
      } else {
        sortByLocationName = (a, b) => {
          return a.locationName.localeCompare(b.locationName);
        };
      }
      const sortedLocation = data.data.sort(sortByLocationName);
      // Update local states
      if (
        AppSettings?.locationBasedOnPickUpAndDropoff &&
        !isAffiliateEnable &&
        AppSettings?.locationReturnEnable
      ) {
        const sortedPickUpLocation = sortedLocation.filter(
          (e) => e.locationType == 3 || e.locationType == 1
        );
        setLocationDetail(sortedPickUpLocation || []);
        const sortedDropOffUpLocation = sortedLocation.filter(
          (e) => e.locationType == 3 || e.locationType == 2
        );
        setDropOffLocationDetail(sortedDropOffUpLocation || []);
      } else {
        setLocationDetail(sortedLocation || []);
        setDropOffLocationDetail(sortedLocation || []);
      }
      // Dispatch to Redux
      if (isAffiliateEnable && !AppSettings?.locationBasedOnPickUpAndDropoff) {
        dispatch(
          BOOKING_ACTIONS.setAffiliateLocationDetails({
            [affiliateId]: sortedLocation || [],
          })
        );
      }
    } catch (error) {
      console.error("Error fetching locations", error);
    }
  };
  function formatTimeInItalianTime(date: any) {
    return new Intl.DateTimeFormat("it-IT", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false, // ensures 24-hour format
    }).format(date);
  }

  function formatDateInItalianTime(date: any) {
    const formatted = new Intl.DateTimeFormat("it-IT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .format(date)
      .split("/")
      .reverse()
      .join("-");
    return formatted;
  }
  const [formattedStartDate, setFormattedStartDate] = useState("");
  const [formattedEndDate, setFormattedEndDate] = useState("");
  useEffect(() => {
    const start_date_time = reservationStartDate?.getTime() || Date.now();
    const end_date_time =
      reservationEndDate?.getTime() || start_date_time + 24 * 60 * 60 * 1000;
    const start_date = new Date(start_date_time);
    const end_date = new Date(end_date_time);
    const formattedStartDate = formatDateInItalianTime(start_date);
    const formattedEndDate = formatDateInItalianTime(end_date);
    setFormattedStartDate(formattedStartDate);
    setFormattedEndDate(formattedEndDate);
  }, [reservationStartDate, reservationEndDate]);

  const fetchResSourceApi = async (affiliateId: string) => {
    try {
      const data = (await BookingApi.getAllReservationSources(
        affiliateId || ""
      )) as BookingSourceResponse[];
      if (data && data.length > 0) {
        const sortedResSource = data
          .filter((item) => item && item.Id !== undefined)
          .sort((a, b) => {
            const labelA = (
              a["Ragione Sociale textRS"] ||
              a["Ragione Sociale"] ||
              ""
            ).trim();
            const labelB = (
              b["Ragione Sociale textRS"] ||
              b["Ragione Sociale"] ||
              ""
            ).trim();

            const startsWithNumA = /^\d/.test(labelA);
            const startsWithNumB = /^\d/.test(labelB);

            if (startsWithNumA && startsWithNumB) {
              const numA = parseInt(labelA.match(/^\d+/)[0], 10);
              const numB = parseInt(labelB.match(/^\d+/)[0], 10);

              if (numA !== numB) {
                return numA - numB;
              }
              return labelA.localeCompare(labelB);
            }
            if (startsWithNumA) return -1;
            if (startsWithNumB) return 1;

            return labelA.localeCompare(labelB);
          });
        setReservationSourceOptions(sortedResSource);
        setReseSourceMacroOptions(sortedResSource);
      }
      if (
        AppSettings?.enablePerDayReservationSource &&
        userId == null &&
        AppSettings?.showReservationGroupSelection
      ) {
        const start_date_time = reservationStartDate?.getTime() || Date.now();
        const end_date_time =
          reservationEndDate?.getTime() ||
          start_date_time + 24 * 60 * 60 * 1000;
        let startTime = formatTimeInItalianTime(new Date(start_date_time));
        let endTime = formatTimeInItalianTime(new Date(end_date_time));
        if (reservationEndDate == null) {
          startTime = "";
          endTime = "";
        }
        const e = (await BookingApi.getReservationSourcesOnStartAndEndDate(
          formattedStartDate,
          formattedEndDate,
          locationLang,
          startTime,
          endTime
        )) as BookingSourceResponse[];
        const data = e?.data;
        const withoutStartEndResSource =
          (await BookingApi.getAllReservationSources(
            affiliateId || ""
          )) as BookingSourceResponse[];
        const checkData = data.length > 0 ? data : withoutStartEndResSource;
        const sortedResSource = checkData
          .filter(
            (item) => item && (item.id !== undefined || item.Id !== undefined)
          )
          .sort((a, b) => {
            const labelA = (
              a["Ragione Sociale textRS"] ||
              a["Ragione Sociale"] ||
              ""
            ).trim();
            const labelB = (
              b["Ragione Sociale textRS"] ||
              b["Ragione Sociale"] ||
              ""
            ).trim();

            const startsWithNumA = /^\d/.test(labelA);
            const startsWithNumB = /^\d/.test(labelB);

            if (startsWithNumA && startsWithNumB) {
              const numA = parseInt(labelA.match(/^\d+/)[0], 10);
              const numB = parseInt(labelB.match(/^\d+/)[0], 10);

              if (numA !== numB) {
                return numA - numB;
              }
              return labelA.localeCompare(labelB);
            }
            if (startsWithNumA) return -1;
            if (startsWithNumB) return 1;

            return labelA.localeCompare(labelB);
          });
        if (reservationEndDate && reservationStartDate) {
          setReservationSourceOptions(sortedResSource);
          setReseSourceMacroOptions(sortedResSource);

          if (
            sortedResSource.length === 0 ||
            !sortedResSource.some(
              (option: any) =>
                (option["Ragione Sociale textRS"] ||
                  option["Ragione Sociale"]) === reservationPlatform
            )
          ) {
            setReservationPlatform("");
          }
        }
      }
    } catch (error) {
      console.error("Error fetching reservation sources", error);
    }
  };

  useEffect(() => {
    const resSourceDetails = reduxBookingDetails.reservationSource.name;
    if (resSourceDetails == "" && !scriptChannel) {
      if (userId == null) {
        const foundOption = reservationSourceOptions.find(
          (e) => e["Ragione Sociale textRS"]
        );
        const res = foundOption ? foundOption["Ragione Sociale textRS"] : "";
        setReservationPlatform(res);
      } else {
        const foundOption = reservationSourceForUser?.find(
          (e) => e["description"]
        );
        const res = foundOption ? foundOption["description"] : "";
        setReservationPlatform(res);
      }
      if (reservationSourceOptions.length > 0) {
        const resSourceData = reservationSourceOptions.find(
          (e) => e.isDefaultEnable === true
        );
        if (resSourceData) {
          setReservationPlatform(resSourceData["Ragione Sociale textRS"]);
        }
      }
      if (reservationSourceForUser.length > 0) {
        const resSourceData = reservationSourceForUser.find(
          (e) => e.isDefaultEnable === true
        );
        if (resSourceData) {
          setReservationPlatform(resSourceData["Ragione Sociale textRS"]);
        }
      }
    }
  }, [reservationSourceOptions, reservationSourceForUser]);
  const url = new URL(window.location.href).pathname.includes("/embed");
  useEffect(() => {
    // if (isAffiliateEnable === undefined) return;
    const affiliateId = selectedAffiliate?.toString() || "";
    const debounceTimeout = setTimeout(() => {
      const existingLocationDetails = locInfo && locInfo[affiliateId];
      if (isAffiliateEnable && selectedAffiliate) {
        //console.log("if condition excuting");
        if (existingLocationDetails) {
          //console.log("From redux");
          fetchResSourceApi(affiliateId);
          setLocationDetail(existingLocationDetails || []);
        } else {
          //console.log("Not From redux");
          fetchLocationApi(affiliateId);
          fetchResSourceApi(affiliateId);
        }
      } else if (
        (selectedAffiliate == undefined || selectedAffiliate == "null") &&
        (isAffiliateEnable == false || isAffiliateEnable == null)
      ) {
        fetchLocationApi("");
        fetchResSourceApi("");
      }
    }, 10);
    return () => clearTimeout(debounceTimeout);
  }, [
    selectedAffiliate,
    isAffiliateEnable,
    url,
    locInfo,
    locationLang,
    reservationEndDate,
    reservationStartDate,
  ]);

  useEffect(() => {
    if (filterViaMacro && selectedMacroGroup) {
      const fetchData = async () => {
        try {
          const data = (await BookingApi.getAllMacroReservationSources(
            selectedMacroGroup || ""
          )) as BookingSourceResponse[];

          const reservationSource = data
            .filter((item) => item?.Id !== undefined)
            .map((item) => item.Id);
          if (
            selectedAffiliate &&
            isAffiliateEnable &&
            reservationSourceOptions
          ) {
            if (isUserValid && resSourceMacroForUser?.length > 0) {
              const commonSources = reseSourceMacroOptions.filter((option) =>
                data.some((d) => d.Id === option.Id)
              );
              setReservationSourceForUser(commonSources);
            } else {
              setReservationSourceOptions(
                reseSourceMacroOptions.filter((stable) =>
                  data.some((res) => stable.Id === res.Id)
                )
              );
            }
          } else {
            if (isUserValid && resSourceMacroForUser?.length > 0) {
              const filterSources = (sourceList: any[]) =>
                sourceList.filter((stable) =>
                  reseSourceMacroOptions.some((res) => stable.id === res.Id)
                );
              setReservationSourceForUser(filterSources(resSourceMacroForUser));
            } else {
              setReservationSourceOptions(
                reservationSource.length > 0 ? data : []
              );
            }
          }
        } catch (error) {
          console.error("Error fetching reservation sources:", error);
        }
      };

      fetchData();
    }
  }, [
    selectedMacroGroup,
    filterViaMacro,
    isAffiliateEnable,
    selectedAffiliate,
    userId,
  ]);

  useEffect(() => {
    BookingApi.getAllMacroGroups().then((e) => {
      setMacroGroupOptions(e);
    });
  }, []);
  const companyCode = process.env.REACT_APP_COMPANY_CODE == "loverent";
  // ----  set reservationSource from the query parameters for rentalPlusBooking  ----
  useEffect(() => {
    const userId: any = localStorage.getItem("userID");
    if (!userId && scriptChannel && reservationSourceOptions?.length > 0) {
      const reservationSource =
        reservationSourceOptions?.find((e) => e["Codice"] === scriptChannel)?.[
          "Ragione Sociale textRS"
        ] || "";
      if (reservationSource && reservationSource !== "undefined") {
        window.name = scriptChannel; //storing the scriptChannel even when page get refreshed
        sessionStorage.setItem("scriptChannel", scriptChannel);
        setIsScriptChannelResSource(true);
        setReservationPlatform(reservationSource);
      } else {
        setIsInvalidScriptChannelResSource(true);
        setIsScriptChannelResSource(true);
      }
    }
  }, [reservationSourceOptions]);
  // ----------------------- END ----------------------------------

  const validateAgreementCoupon = AppSettings?.validateAgreementCoupon;
  const rateAmount = 0;
  const optionalAmount = 0;
  const totalAmount = 0;
  const refIdContext = 0;

  // Validation function for discount code with API call
  const validateDiscountCode = async (code) => {
    try {
      const response = await BookingApi.couponDiscount(
        code.trim(),
        new Date(reservationStartDate).toISOString(),
        new Date(reservationEndDate).toISOString(),
        rateAmount,
        optionalAmount,
        totalAmount,
        refIdContext
      );

      const responseData = response?.data;
      const nestedData = responseData?.data;
      const status = responseData?.status;
      const bookingStatus = nestedData?.bookingStatus;
      if (status && status !== "error") {
        if (bookingStatus == "error") {
          setShowModal(true);
          setModalMessage(discountExpireText);
          // setModalMessage(translate("attention_discount_expire"));
          return false;
        } else {
          console.log("Coupon applied successfully!");
          return true;
        }
      } else {
        setShowModal(true);
        setModalMessage(discountNotValidText);
        // setModalMessage(
        //   translate(
        //     "discountNotValidText"
        //   )
        // );
        return false;
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      setShowModal(true);
      return false;
    }
  };
  const [pickupLoc, setPickupLoc] = useState<string>("");
  const [dropupLocation, setDropupLocation] = useState<string>("");
  const headerAndFooter = AppSettings?.headerAndFooter;
  useEffect(() => {
    // Store the current value to avoid referencing outdated state
    const locDetails = pickupLoc;

    if (
      !locDetails ||
      !headerAndFooter ||
      !AppSettings?.showLocationWebDescription
    ) {
      return;
    }
    const location = locationDetail?.find(
      (e: Location) =>
        locDetails === e.publicWebDescriptionEN ||
        locDetails === e.publicWebDescription ||
        locDetails === e.locationName
    );
    const DropUpLocation = locationDetail?.find((e: Location) => {
      return (
        (dropupLocation || locDetails) === e.publicWebDescriptionEN ||
        (dropupLocation || locDetails) === e.publicWebDescription ||
        (dropupLocation || locDetails) === e.locationName
      );
    });
    if (location && locationLang) {
      const updatedLocation =
        locationLang === "en"
          ? location.publicWebDescriptionEN || location.locationName
          : location.publicWebDescription || location.locationName;

      if (updatedLocation !== pickupLocation) {
        setPickupLocation(updatedLocation);
      }
    }
    const validLocation = DropUpLocation ?? location;
    if (validLocation && locationLang) {
      const updatedLocation =
        locationLang === "en"
          ? validLocation.publicWebDescriptionEN || validLocation?.locationName
          : validLocation.publicWebDescription || validLocation?.locationName;
      if (updatedLocation !== reservationDestination) {
        setReservationDestination(updatedLocation);
      }
    }
  }, [
    pickupLocation,
    locationDetail,
    headerAndFooter,
    dropupLocation,
    reservationDestination,
    locationLang,
  ]);

  const disableDate =
    pickupLocation && locationDetail.length
      ? (() => {
          const location = locationDetail.find((e) => {
            let optionValue;
            if (AppSettings?.showLocationWebDescription) {
              if (locationLang === "it" && e.publicWebDescription) {
                optionValue = e.publicWebDescription;
              } else if (
                locationLang === "en" &&
                e.publicWebDescriptionEN &&
                AppSettings?.showLocationWebDescription
              ) {
                optionValue = e.publicWebDescriptionEN;
              } else {
                optionValue = e.locationName;
              }
            } else {
              optionValue = [
                e.locationCode,
                "-",
                e.locationName,
                e.locationNumber,
                e.locationAddress,
                e.locationCity,
                e.zipCode,
              ].join(" ");
            }
            return optionValue?.toLowerCase() === pickupLocation?.toLowerCase();
          });

          // If onlyDropOffOutOfHours is true, enable all days
          if (location?.onlyDropOffOutOfHours || location?.isOutOfHours) {
            return [];
          }

          const openDays = location?.openings?.map((o) => o.dayOfTheWeek) || [];
          const allDays = {
            0: true,
            1: true,
            2: true,
            3: true,
            4: true,
            5: true,
            6: true,
          };
          openDays.forEach((d) => {
            const jsDay = d === 7 ? 0 : d; // Convert Sunday(7) to 0 bcoz js day start with sunday but here we have started with monday
            delete allDays[jsDay];
          });

          return Object.keys(allDays).map(Number);
        })()
      : [];

  // const disableDate =
  //   pickupLocation && locationDetail.length
  //     ? Object.keys(
  //         (
  //           locationDetail
  //             .filter((e) => {
  //               const optionValue = AppSettings?.showLocationWebDescription
  //                 ? locationLang === "it" && e.publicWebDescription
  //                   ? e.publicWebDescription
  //                   : locationLang === "en" &&
  //                     e.publicWebDescriptionEN &&
  //                     AppSettings?.showLocationWebDescription
  //                   ? e.publicWebDescriptionEN
  //                   : e.locationName
  //                 : e.locationCode +
  //                   "-" +
  //                   e.locationName +
  //                   " " +
  //                   e.locationNumber +
  //                   " " +
  //                   e.locationAddress +
  //                   " " +
  //                   e.locationCity +
  //                   " " +
  //                   e.zipCode;
  //               return (
  //                 optionValue?.toLowerCase() === pickupLocation?.toLowerCase()
  //               );
  //             })[0]
  //             ?.openings?.map((e) => e.dayOfTheWeek % 7) || []
  //         ) // Fallback to empty array if `openings` is undefined or null
  //           .reduce(
  //             (e, v) => {
  //               delete e[v];
  //               return e;
  //             },
  //             {
  //               0: true,
  //               1: true,
  //               2: true,
  //               3: true,
  //               4: true,
  //               5: true,
  //               6: true,
  //             }
  //           ) || {} // Fallback to an empty object if `reduce` returns `undefined`
  //       ).map((e) => parseInt(e))
  //     : [];

  return (
    <ThemeProvider theme={dynamicTheme}>
      {isModalLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(1px)",
            WebkitBackdropFilter: "blur(1px)",
          }}
        >
          <CircularProgress
            style={{ color: dynamicTheme?.palette["primary"]?.main || "white" }}
          />
        </div>
      )}

      <main
        className={`h-full ${
          companyCode ? "" : "flex justify-center"
        } box-border ${className}`}
      >
        <form
          onSubmit={async (e) => {
            // onSubmit={(e) => {
            e.preventDefault();
            if (agreementCoupon.trim() && validateAgreementCoupon) {
              const isCouponValid = await validateDiscountCode(agreementCoupon);
              if (!isCouponValid) {
                return;
              }
            }
            if (
              AppSettings?.minLeadTimeInHours &&
              AppSettings?.minLeadTimeInHours > 0
            ) {
              const start_date_time =
                reservationStartDate?.getTime() || Date.now();
              const parsedStartDate = new Date(start_date_time);
              const now = new Date();
              const nowWithLead = new Date(now);
              nowWithLead.setHours(
                now.getHours() + AppSettings.minLeadTimeInHours
              );
              const formattedDateTime = nowWithLead.toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false, // for 24-hour format
              });
              if (parsedStartDate < nowWithLead) {
                setShowModal(true);
                const modalMessage = String(leadTimeAlertMsg).replace(
                  "{timeValue}",
                  formattedDateTime
                );
                setModalMessage(modalMessage);
                return;
              }
            }
            let selectedRS;
            if (reservationSourceEnabled) {
              selectedRS = reservationSourceForUser?.find(
                (e) =>
                  e.description === reservationPlatform ||
                  e["Ragione Sociale textRS"] === reservationPlatform ||
                  e["Ragione Sociale"] === reservationPlatform
              );
            } else {
              selectedRS = reservationSourceOptions?.find(
                (e) =>
                  e["Ragione Sociale textRS"] === reservationPlatform ||
                  e["Ragione Sociale"] === reservationPlatform
              );
            }
            const selectedPickupLocation = locationDetail.filter((e) => {
              const optionValue = AppSettings?.showLocationWebDescription
                ? locationLang === "it" && e.publicWebDescription
                  ? e.publicWebDescription
                  : locationLang === "en" &&
                    e.publicWebDescriptionEN &&
                    AppSettings?.showLocationWebDescription
                  ? e.publicWebDescriptionEN
                  : e.locationName
                : e.locationCode +
                  "-" +
                  e.locationName +
                  " " +
                  e.locationNumber +
                  " " +
                  e.locationAddress +
                  " " +
                  e.locationCity +
                  " " +
                  e.zipCode;
              return optionValue === pickupLocation;
            })[0];
            const selectedDropoffLocation = locationDetail.filter((e) => {
              const optionValue = AppSettings?.showLocationWebDescription
                ? locationLang === "it" && e.publicWebDescription
                  ? e.publicWebDescription
                  : locationLang === "en" &&
                    e.publicWebDescriptionEN &&
                    AppSettings?.showLocationWebDescription
                  ? e.publicWebDescriptionEN
                  : e.locationName
                : e.locationCode +
                  "-" +
                  e.locationName +
                  " " +
                  e.locationNumber +
                  " " +
                  e.locationAddress +
                  " " +
                  e.locationCity +
                  " " +
                  e.zipCode;
              return optionValue === reservationDestination;
            })[0];
            const bookingDetailsForLinkDirection: BookingDetails = {
              dropOffDateAndTime: reservationEndDate?.getTime() || Date.now(),
              dropOffLocationDetails: {
                name: reservationDestination,
                code: selectedDropoffLocation?.locationCode,
                locationCity: selectedDropoffLocation?.locationCity,
                locationAddress: selectedDropoffLocation?.locationAddress,
                locationNumber: selectedDropoffLocation?.locationNumber,
                zipCode: selectedDropoffLocation?.zipCode,
                publicWebDescription:
                  selectedDropoffLocation?.publicWebDescription,
                publicWebDescriptionEN:
                  selectedDropoffLocation?.publicWebDescriptionEN,
                reservationSourcePayNow: selectedRS?.payNow ?? false,
                reservationSourcePayLater: selectedRS?.payLater ?? false,
              },
              dropOffLocationName: reservationDestination,
              pickUpLocationDetails: {
                name: pickupLocation,
                code: selectedPickupLocation?.locationCode,
                locationCity: selectedPickupLocation?.locationCity,
                locationAddress: selectedPickupLocation?.locationAddress,
                locationNumber: selectedPickupLocation?.locationNumber,
                zipCode: selectedPickupLocation?.zipCode,
                publicWebDescription:
                  selectedPickupLocation?.publicWebDescription,
                publicWebDescriptionEN:
                  selectedPickupLocation?.publicWebDescriptionEN,
                reservationSourcePayNow: selectedRS?.payNow ?? false,
                reservationSourcePayLater: selectedRS?.payLater ?? false,
                isAirport: selectedPickupLocation?.isAirport,
              },
              affiliateDetails: {
                name: selectedAffiliateName,
                code: selectedAffiliate || "",
              },
              pickUpLocationName: pickupLocation,
              pickUpDateAndTime: reservationStartDate?.getTime() || Date.now(),
              reservationSource: {
                name: reservationPlatform,
                code:
                  selectedRS?.Codice ||
                  selectedRS?.code ||
                  ((isInvalidScriptChannelResSource && scriptChannel) ??
                    scriptChannel) ||
                  "",
                reservationSourcePayNow: selectedRS?.payNow ?? false,
                reservationSourcePayLater: selectedRS?.payLater ?? false,
                locationCity: "",
                locationAddress: "",
                locationNumber: "",
                zipCode: "",
                publicWebDescription: "",
                publicWebDescriptionEN: "",
              },
              agreementCoupon: agreementCoupon.trim(),
              discountValueWithoutVat: discountValueWithoutVat,
              macroGroupName: selectedMacroGroupWebCode,
              showPics: true,
              showOptionalImage: true,
              showVehicleParameter: true,
              showVehicleExtraImage: true,
              showBookingDiscount: true,
              isYoungDriverAge: isYoungDriverAge,
              isYoungDriver2Age: youngDriver2,
              isSeniorDriverAge: isSeniorDriverAge,
              noFeeAge: isNoFeeAge,
              isAgeLimitSelected: isAgeLimitSelected,
              payLaterFeeAmt: "",
              isPayLaterFee: false,
              dropAtSameLocation: isDroppingAtSameLocation,
              reservationSourcePayNow: selectedRS?.payNow ?? false,
              reservationSourcePayLater: selectedRS?.payLater ?? false,
              youngDriverAgeValue: ageCriteriaData?.["youngDriver"],
              youngDriver2AgeValue: ageCriteriaData?.["youngDriver2"],
              seniorDriverAgeValue: ageCriteriaData?.["seniorDriver"],
              noAgeDriverValue: ageCriteriaData?.["noAgeDriver"],
              scriptChannel:
                queryParams.get("scriptChannel") &&
                isScriptChannelResSource &&
                !userId
                  ? queryParams.get("scriptChannel")
                  : "",
              isInvalidScriptChannelResSource: isInvalidScriptChannelResSource,
              scriptAffiliate: queryParams.get("scriptAffiliate")
                ? queryParams.get("scriptAffiliate")
                : "",
              scriptVoucherCode: queryParams.get("scriptVoucherCode")
                ? queryParams.get("scriptVoucherCode")
                : "",
              reservedAreaPayNow: reservedAreaPayNow || false,
            };
            onSubmit(bookingDetailsForLinkDirection);
          }}
        >
          {isEnableHorizontalView === false ? (
            <>
              {AppSettings?.pageTitle && (
                <>
                  <span className="text-white font-bold text-xl">
                    {t("Book Online")}
                  </span>
                  <span className="text-white font-bold text-xl">-</span>
                  <span className="text-white font-bold text-xl">
                    {pageTitleLabel}
                  </span>
                  <br />
                  <br />
                </>
              )}
              <AppCard className="flex flex-col bg-white w-full box-border justify-center items-center p-4 gap-4 py-8">
                <div className="flex xl:flex-row gap-2 flex-col w-full">
                  {isAffiliateEnable && !scriptAffiliate && (
                    <AppInputSwitcher
                      required={true}
                      className={
                        "md:grow flex md:flex-row gap-2 flex-col min-w-[0rem] md:min-w-[19rem]"
                      }
                      value={selectedAffiliateName}
                      label={translate("Choose the partner to rent with")}
                      key={"affiliate"}
                      options={affiliateOptions}
                      type="select"
                      variant="with-search"
                      disabled={true}
                      onchange={(selectedName) => {
                        const selectedAffiliate = affiliateDetail.find(
                          (e) => e[1] === selectedName
                        );
                        if (selectedAffiliate) {
                          const id = selectedAffiliate[0];
                          const name = selectedAffiliate[1];
                          setSelectedAffiliateName(name);
                          setSelectedAffiliate(id);
                          setPickupLocation(null);
                          setStartDate(null);
                          setEndDate(null);
                        }
                      }}
                    />
                  )}
                  <div
                    className={`md:grow flex md:flex-row gap-2 flex-col min-w-[0rem] ${
                      isAffiliateEnable
                        ? "md:min-w-[25rem]"
                        : "md:min-w-[25rem]"
                    }`}
                  >
                    <AppInputSwitcher
                      required={true}
                      className={"w-full"}
                      value={pickupLocation}
                      label={translate("pick up location")}
                      key={"pickUpLocation"}
                      options={locationDetail?.map((e: Location) => {
                        const optionValue =
                          AppSettings?.showLocationWebDescription
                            ? locationLang === "it" && e.publicWebDescription
                              ? e.publicWebDescription
                              : locationLang === "en" &&
                                e.publicWebDescriptionEN &&
                                AppSettings?.showLocationWebDescription
                              ? e.publicWebDescriptionEN
                              : e.locationName
                            : e.locationCode +
                              "-" +
                              e.locationName +
                              " " +
                              e.locationNumber +
                              " " +
                              e.locationAddress +
                              " " +
                              e.locationCity +
                              " " +
                              e.zipCode;

                        let icon = <LocationCityIcon color="primary" />;
                        if (e.isAirport) {
                          icon = <FlightTakeoffIcon htmlColor="blue" />;
                        } else if (e.isRailway) {
                          icon = <TrainIcon htmlColor="green" />;
                        }
                        return {
                          label: optionValue,
                          value: optionValue,
                          icon,
                        };
                      })}
                      type="select"
                      variant={
                        AppSettings?.notOpenKeyboardForLocation && smallScreen
                          ? "with-search-label"
                          : "with-search"
                      }
                      onchange={(e) => {
                        if (isDroppingAtSameLocation) {
                          setReservationDestination(e);
                        }
                        setPickupLoc(e);
                        setPickupLocation(e);
                        setStartDate(null);
                        setEndDate(null);
                      }}
                    />
                    {!isDroppingAtSameLocation && (
                      <AppInputSwitcher
                        required={true}
                        value={reservationDestination}
                        label={translate("drop off location")}
                        key={"dropOffLocation"}
                        options={locationDropOffDetail.map((e) => {
                          const optionValue =
                            AppSettings?.showLocationWebDescription
                              ? locationLang === "it" && e.publicWebDescription
                                ? e.publicWebDescription
                                : locationLang === "en" &&
                                  e.publicWebDescriptionEN &&
                                  AppSettings?.showLocationWebDescription
                                ? e.publicWebDescriptionEN
                                : e.locationName
                              : e.locationCode +
                                "-" +
                                e.locationName +
                                " " +
                                e.locationNumber +
                                " " +
                                e.locationAddress +
                                " " +
                                e.locationCity +
                                " " +
                                e.zipCode;
                          let icon = <LocationCityIcon color="primary" />;
                          if (e.isAirport) {
                            icon = <FlightTakeoffIcon htmlColor="blue" />;
                          } else if (e.isRailway) {
                            icon = <TrainIcon htmlColor="green" />;
                          }
                          return {
                            label: optionValue,
                            value: optionValue,
                            icon,
                          };
                        })}
                        type="select"
                        variant="with-search"
                        onchange={(e) => {
                          setReservationDestination(e);
                          setDropupLocation(e);
                          setEndDate(null);
                        }}
                      />
                    )}
                  </div>

                  {AppSettings?.showSeparatePickDropDate ? (
                    <>
                      {/* Start Date Picker */}
                      <DatePickerComponent
                        sx={{
                          svg: { color: "primary" },
                          input: { color: "primary" },
                          label: { color: "primary" },
                          backgroundColor: "primary",
                        }}
                        required={true}
                        disableOnDay={disableDate}
                        className="w-full grow md:min-w-36"
                        label={pickUpDate}
                        value={reservationStartDate}
                        minDate={dayjs().add(minRentalAllowedDay, "day")}
                        maxDate={dayjs().add(365, "day")}
                        onChange={(selectedStartDate) => {
                          if (selectedStartDate) {
                            setDateError("");
                            const selectedLocation = locationDetail.find(
                              (e) => {
                                const optionValue =
                                  AppSettings?.showLocationWebDescription
                                    ? locationLang === "it" &&
                                      e.publicWebDescription
                                      ? e.publicWebDescription
                                      : locationLang === "en" &&
                                        e.publicWebDescriptionEN &&
                                        AppSettings?.showLocationWebDescription
                                      ? e.publicWebDescriptionEN
                                      : e.locationName
                                    : `${e.locationCode}-${e.locationName} ${e.locationNumber} ${e.locationAddress} ${e.locationCity} ${e.zipCode}`;
                                return (
                                  optionValue?.toLowerCase() ===
                                  pickupLocation?.toLowerCase()
                                );
                              }
                            );

                            if (
                              selectedLocation &&
                              selectedLocation.onlyDropOffOutOfHours &&
                              Array.isArray(selectedLocation.closing)
                            ) {
                              const startDay = getDay(
                                selectedStartDate.toDate()
                              );

                              // Check if the selected date falls in any closed period
                              const isDateClosed =
                                selectedLocation.closing.some((entry) => {
                                  if (entry.dayOfTheWeek % 7 === startDay) {
                                    if (entry.validFrom && entry.validTo) {
                                      const validFrom = new Date(
                                        entry.validFrom
                                      );
                                      const validTo = new Date(entry.validTo);
                                      const selectedDate =
                                        selectedStartDate.toDate();

                                      return (
                                        selectedDate >= validFrom &&
                                        selectedDate <= validTo
                                      );
                                    }
                                    return true;
                                  }
                                  return false;
                                });

                              if (isDateClosed) {
                                setDateError(
                                  "The date is not available for booking."
                                );
                                setStartDate(null);
                                return;
                              }
                            }
                            // if (
                            //   selectedLocation &&
                            //   selectedLocation.onlyDropOffOutOfHours &&
                            //   Array.isArray(selectedLocation.closing)
                            // ) {
                            //   const startDay = getDay(
                            //     selectedStartDate.toDate()
                            //   );
                            //   const isClosed = selectedLocation.closing.some(
                            //     (entry) => entry.dayOfTheWeek % 7 === startDay
                            //   );
                            //   const isOpened = selectedLocation.openings.some(
                            //     (entry) => entry.dayOfTheWeek % 7 === startDay
                            //   );
                            //
                            //   if (isClosed || !isOpened) {
                            //     setDateError(
                            //       "The Start date is not available for booking."
                            //     );
                            //     setStartDate(null);
                            //     return;
                            //   } else {
                            //     setDateError("");
                            //   }
                            // }

                            // const opening = selectedLocation?.openings?.find(
                            //   (e) =>
                            //     getDay(selectedStartDate.toDate()) ===
                            //     e.dayOfTheWeek % 7
                            // )?.startTime;
                            // setStartDate(
                            //   setTime(opening || "", selectedStartDate.toDate())
                            // );

                            if (selectedLocation) {
                              const openingObj =
                                selectedLocation.openings?.find(
                                  (e) =>
                                    getDay(selectedStartDate.toDate()) ===
                                    e.dayOfTheWeek % 7
                                );

                              const opening = selectedLocation.isOutOfHours
                                ? openingObj?.LocationStartTime
                                : openingObj?.startTime;

                              setStartDate(
                                setTime(
                                  opening || "",
                                  selectedStartDate.toDate()
                                )
                              );
                            }
                          } else {
                            setStartDate(null);
                          }
                        }}
                      />
                      {/* End Date Picker */}
                      <DatePickerComponent
                        sx={{
                          svg: { color: "primary" },
                          input: { color: "primary" },
                          label: { color: "primary" },
                          backgroundColor: "primary",
                        }}
                        className="w-full grow md:min-w-36"
                        required={true}
                        disableOnDay={disableDate}
                        label={dropOffDate}
                        value={reservationEndDate}
                        minDate={
                          reservationStartDate
                            ? dayjs(reservationStartDate)
                            : dayjs().add(minRentalAllowedDay, "day")
                        }
                        maxDate={
                          reservationStartDate
                            ? dayjs(reservationStartDate).add(
                                maxRentalAllowedDay,
                                "day"
                              )
                            : dayjs().add(365, "day")
                        }
                        onChange={(selectedEndDate) => {
                          if (selectedEndDate) {
                            const selectedLocation = locationDetail.find(
                              (e) => {
                                const optionValue =
                                  AppSettings?.showLocationWebDescription
                                    ? locationLang === "it" &&
                                      e.publicWebDescription
                                      ? e.publicWebDescription
                                      : locationLang === "en" &&
                                        e.publicWebDescriptionEN &&
                                        AppSettings?.showLocationWebDescription
                                      ? e.publicWebDescriptionEN
                                      : e.locationName
                                    : `${e.locationCode}-${e.locationName} ${e.locationNumber} ${e.locationAddress} ${e.locationCity} ${e.zipCode}`;
                                return (
                                  optionValue?.toLowerCase() ===
                                  reservationDestination?.toLowerCase()
                                );
                              }
                            );

                            const opening = selectedLocation?.openings?.find(
                              (e) =>
                                getDay(selectedEndDate.toDate()) ===
                                e.dayOfTheWeek % 7
                            )?.startTime;
                            setEndDate(
                              setTime(opening || "", selectedEndDate.toDate())
                            );
                          } else {
                            setEndDate(null);
                          }
                        }}
                      />
                    </>
                  ) : (
                    <AppInputSwitcher
                      disableOnDay={disableDate}
                      required={true}
                      className="w-full grow md:min-w-72"
                      label={translate("reservation dates")}
                      key={"reservationDates"}
                      options={[]}
                      startDate={reservationStartDate}
                      endDate={reservationEndDate}
                      minDate={addDays(new Date(), minRentalAllowedDay)}
                      maxDate={
                        reservationStartDate
                          ? addDays(
                              new Date(reservationStartDate?.toString()),
                              maxRentalAllowedDay
                            )
                          : addDays(new Date(), 365)
                      }
                      type="date"
                      variant="range"
                      onchange={(selectedDateRange) => {
                        if (selectedDateRange[0]) {
                          setDateError("");
                          const selectedLocation = locationDetail.filter(
                            (e) => {
                              const optionValue =
                                AppSettings?.showLocationWebDescription
                                  ? locationLang === "it" &&
                                    e.publicWebDescription
                                    ? e.publicWebDescription
                                    : locationLang === "en" &&
                                      e.publicWebDescriptionEN &&
                                      AppSettings?.showLocationWebDescription
                                    ? e.publicWebDescriptionEN
                                    : e.locationName
                                  : e.locationCode +
                                    "-" +
                                    e.locationName +
                                    " " +
                                    e.locationNumber +
                                    " " +
                                    e.locationAddress +
                                    " " +
                                    e.locationCity +
                                    " " +
                                    e.zipCode;
                              return (
                                optionValue?.toLowerCase() ===
                                pickupLocation?.toLowerCase()
                              );
                            }
                          )[0];

                          if (
                            selectedLocation &&
                            selectedLocation.onlyDropOffOutOfHours &&
                            Array.isArray(selectedLocation.closing)
                          ) {
                            const startDay = getDay(selectedDateRange[0]);

                            // Check if the selected date falls in any closed period
                            const isDateClosed = selectedLocation.closing.some(
                              (entry) => {
                                if (entry.dayOfTheWeek % 7 === startDay) {
                                  if (entry.validFrom && entry.validTo) {
                                    const validFrom = new Date(entry.validFrom);
                                    const validTo = new Date(entry.validTo);
                                    const selectedDate = selectedDateRange[0];

                                    return (
                                      selectedDate >= validFrom &&
                                      selectedDate <= validTo
                                    );
                                  }
                                  return true;
                                }
                                return false;
                              }
                            );

                            if (isDateClosed) {
                              setDateError(
                                "The date is not available for booking."
                              );
                              setStartDate(null);
                              return;
                            }
                          }
                          // if (
                          //   selectedLocation &&
                          //   selectedLocation.onlyDropOffOutOfHours &&
                          //   Array.isArray(selectedLocation.closing)
                          // ) {
                          //   const startDay = getDay(selectedDateRange[0]);
                          //   const isClosed = selectedLocation.closing.some(
                          //     (entry) => entry.dayOfTheWeek % 7 === startDay
                          //   );
                          //   const isOpened = selectedLocation.openings.some(
                          //     (entry) => entry.dayOfTheWeek % 7 === startDay
                          //   );
                          //
                          //   if (isClosed || !isOpened) {
                          //     setDateError(
                          //       "The date is not available for booking."
                          //     );
                          //     setStartDate(null);
                          //     return;
                          //   } else {
                          //     setDateError("");
                          //   }
                          // }

                          if (selectedLocation) {
                            const opening = selectedLocation.openings.filter(
                              (e) =>
                                getDay(selectedDateRange[0]) ===
                                e.dayOfTheWeek % 7
                            )[0];

                            const startTime = opening
                              ? opening.LocationStartTime || opening.startTime
                              : "";

                            setStartDate(
                              setTime(startTime, selectedDateRange[0])
                            );
                          }
                        } else {
                          setStartDate(null);
                        }
                        if (selectedDateRange[1]) {
                          const selectedLocation = locationDetail.filter(
                            (e) => {
                              const optionValue =
                                AppSettings?.showLocationWebDescription
                                  ? locationLang === "it" &&
                                    e.publicWebDescription
                                    ? e.publicWebDescription
                                    : locationLang === "en" &&
                                      e.publicWebDescriptionEN &&
                                      AppSettings?.showLocationWebDescription
                                    ? e.publicWebDescriptionEN
                                    : e.locationName
                                  : e.locationCode +
                                    "-" +
                                    e.locationName +
                                    " " +
                                    e.locationNumber +
                                    " " +
                                    e.locationAddress +
                                    " " +
                                    e.locationCity +
                                    " " +
                                    e.zipCode;
                              return (
                                optionValue?.toLowerCase() ===
                                reservationDestination.toLowerCase()
                              );
                            }
                          )[0];

                          if (selectedLocation) {
                            const opening = selectedLocation.openings.filter(
                              (e) =>
                                getDay(selectedDateRange[1]) ===
                                e.dayOfTheWeek % 7
                            )[0];

                            const endTime = opening
                              ? opening.LocationStartTime || opening.startTime
                              : "";

                            setEndDate(setTime(endTime, selectedDateRange[1]));
                          }
                        } else {
                          setEndDate(null);
                        }
                      }}
                    />
                  )}
                  <AppInputSwitcher
                    required={true}
                    disabled={!reservationStartDate}
                    // className="w-[49%] min-w-[5rem]"
                    className="w-full lg:!w-[100%] min-w-[5rem] sm:w-[100%] md:w-full"
                    label={pickUpTime}
                    key={"pickUpTime"}
                    enableTimesInRange={getEnabledTimeSlots(
                      locationDetail,
                      pickupLocation,
                      reservationStartDate
                    )}
                    value={reservationStartDate}
                    options={[]}
                    type="time"
                    variant="slot"
                    onchange={(e) => {
                      setStartDate(e);
                    }}
                  />
                  <AppInputSwitcher
                    enableTimesInRange={getEnabledDropoffTimeSlots(
                      locationDetail,
                      reservationDestination,
                      reservationEndDate
                    )}
                    disabled={!reservationEndDate}
                    required={true}
                    className="w-full lg:!w-[100%]  min-w-[5rem] sm:w-[100%] md:w-full"
                    label={dropOffTime}
                    key={"dropOffTime"}
                    options={[]}
                    value={reservationEndDate}
                    type="time"
                    variant="slot"
                    onchange={(e) => {
                      setEndDate(e);
                    }}
                  />
                  {AppSettings?.showMacroGroupSelection && (
                    <AppInputSwitcher
                      required={false}
                      className={"w-full"}
                      type="select"
                      variant="with-search"
                      label={macroGroupLabel}
                      options={(Array.isArray(macroGroupOptions)
                        ? macroGroupOptions
                        : []
                      ).map((e) => e.label)}
                      value={selectedMacroGroup}
                      onchange={(e) => {
                        const selectedOption = Array.isArray(macroGroupOptions)
                          ? macroGroupOptions.find(
                              (option) => option.label === e
                            )
                          : null;
                        if (selectedOption) {
                          setSelectedMacroGroup(selectedOption.label);
                          setSelectedMacroGroupWebCode(selectedOption.WebCode);
                        }
                      }}
                    />
                  )}

                  {disableCoupon === false &&
                    (mountedRef.current &&
                    AppSettings?.showReservationGroupSelection &&
                    reservationSourceEnabled === true &&
                    reservationSourceForUser?.length > 0 ? (
                      <div className="flex">
                        <AppInputSwitcher
                          required={true}
                          className="w-full min-w-[12rem]"
                          enablePerDayReservationSource={
                            AppSettings?.enablePerDayReservationSource
                          }
                          label={
                            userId
                              ? bookingResSourceForReservedArea
                              : bookingReservationSource
                          }
                          key={"reservationPlatform"}
                          options={reservationSourceForUser?.map(
                            (e) =>
                              e["Ragione Sociale textRS"] ||
                              e["description"] ||
                              e["Ragione Sociale"]
                          )}
                          type="select"
                          variant="with-search"
                          onchange={(e) => {
                            setReservationPlatform(e);
                          }}
                          value={reservationPlatform}
                        />
                      </div>
                    ) : (
                      AppSettings?.showReservationGroupSelection &&
                      !isScriptChannelResSource && (
                        <div className="flex">
                          <AppInputSwitcher
                            required={true}
                            enablePerDayReservationSource={
                              AppSettings?.enablePerDayReservationSource
                            }
                            className="w-full min-w-[12rem]"
                            label={bookingReservationSource}
                            key={"reservationPlatform"}
                            options={reservationSourceOptions?.map(
                              (e) =>
                                e["Ragione Sociale textRS"] ||
                                e["Ragione Sociale"]
                            )}
                            value={reservationPlatform}
                            type="select"
                            variant="with-search"
                            onchange={(e) => {
                              setReservationPlatform(e);
                            }}
                          />
                        </div>
                      )
                    ))}

                  {AppSettings?.showWebCodeInput && (
                    <AppInputSwitcher
                      required={false}
                      autoComplete="off"
                      className="min-w-[10rem]"
                      label={codiceLabel}
                      key={"Voucher Code"}
                      placeholder={codiceLabel}
                      options={[]}
                      value={agreementCoupon}
                      type="text"
                      variant="slot"
                      onchange={(e) => {
                        setagreementCoupon(e);
                      }}
                      disabledCoupon={disableCoupon}
                    />
                  )}
                  {/* Modal for invalid discount code */}
                  {showModal && validateAgreementCoupon && (
                    <div
                      className="modal-overlay"
                      style={{
                        position: "fixed",
                        top: "0",
                        left: "0",
                        right: "0",
                        bottom: "0",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: "1001",
                      }}
                    >
                      <div
                        className="modal-content"
                        style={{
                          background: "white",
                          width: "300px",
                          padding: "15px",
                          borderRadius: "8px",
                          textAlign: "center",
                          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                          zIndex: "1000",
                        }}
                      >
                        <p>
                          {modalMessage}
                          {/*{translate(*/}
                          {/*  "discountNotValidText"*/}
                          {/*)}*/}
                        </p>
                        <button
                          onClick={() => setShowModal(false)}
                          className="close-button"
                          style={{
                            backgroundColor:
                              dynamicTheme?.palette["primary"]?.main,
                            color: "white",
                            border: "none",
                            padding: "10px 20px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            marginTop: "10px",
                          }}
                        >
                          {translate("Close")}
                          {/*Close*/}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <>
                  {dateError && (
                    <div className="text-red-500 text-sm mt-1">{dateError}</div>
                  )}
                </>
                <div className="flex flex-row gap-2 w-full flex-wrap">
                  {AppSettings?.locationReturnEnable && (
                    <div className="min-w-fit max-w-fit">
                      <AppStandardMultiSelectCheckBox
                        value={[
                          isDroppingAtSameLocation
                            ? translate("drop at same location")
                            : "",
                        ]}
                        onChange={(e) => {
                          setIsDroppingAtSameLocation(
                            !isDroppingAtSameLocation
                          );
                          if (e.length) {
                            setReservationDestination(pickupLocation);
                          }
                        }}
                        options={[translate("drop at same location")]}
                        color={
                          AppSettings?.secondaryAsBackgroundForButtons
                            ? "secondaryColor"
                            : "primary"
                        }
                      />
                    </div>
                  )}

                  <div>
                    <AppRadioButton
                      required={true}
                      value={isAgeLimitSelected}
                      color={
                        AppSettings?.secondaryAsBackgroundForButtons
                          ? "secondaryColor"
                          : "primary"
                      }
                      onChange={(e) => {
                        setIsAgeLimitSelected(e);
                      }}
                      options={Object.entries(ageCriteriaData)
                        .map(([key, value]) => {
                          switch (key.toUpperCase()) {
                            case "YOUNGDRIVER":
                              return {
                                label: t(value),
                                value: "youngDriver",
                              };
                            case "YOUNGDRIVER2":
                              return {
                                label: t(value),
                                value: "youngDriver2",
                              };
                            case "NOAGEDRIVER":
                              return {
                                label: t(value),
                                value: "noAgeFeeDriver",
                              };
                            case "SENIORDRIVER":
                              return {
                                label: t(value),
                                value: "seniorDriver",
                              };
                            default:
                              return {
                                label: "",
                                value: "",
                              };
                          }
                        })
                        .filter((option) => option !== null)}
                    />
                  </div>
                  <AppButton
                    type={"submit"}
                    endIcon={<SearchIcon />}
                    className={`h-14 md:float-right md:ml-auto ${
                      smallScreen || istabletScreen ? "w-full" : "w-[10%]"
                    }`}
                    color={"primary"}
                    style={{
                      borderRadius: `${AppSettings?.buttonBorderRadius || 0}px`,
                    }}
                  >
                    <p className={`w-full text-center`}>
                      {translate("Search")}
                    </p>
                  </AppButton>
                  {AppSettings?.showTextBeginningOfBookingForm && (
                    <div className="w-full">
                      <p
                        className="self-start"
                        style={{
                          color: dynamicTheme?.palette["secondaryColor"]?.main,
                        }}
                      >
                        {showTextBeginningOfBookingFormMsg}
                      </p>
                    </div>
                  )}
                </div>
              </AppCard>
            </>
          ) : (
            <AppCard className="flex flex-col bg-white w-full box-border justify-center items-center p-4 gap-4 py-8">
              <div className="gap-6 flex-col w-full">
                <div className="">
                  <div style={{ margin: "12px" }}>
                    <AppInputSwitcher
                      required={true}
                      className={"w-full"}
                      value={pickupLocation}
                      label={translate("pick up location")}
                      key={"pickUpLocation"}
                      options={locationDetail?.map((e: Location) => {
                        const optionValue =
                          AppSettings?.showLocationWebDescription
                            ? locationLang === "it" && e.publicWebDescription
                              ? e.publicWebDescription
                              : locationLang === "en" &&
                                e.publicWebDescriptionEN &&
                                AppSettings?.showLocationWebDescription
                              ? e.publicWebDescriptionEN
                              : e.locationName
                            : e.locationCode +
                              "-" +
                              e.locationName +
                              " " +
                              e.locationNumber +
                              " " +
                              e.locationAddress +
                              " " +
                              e.locationCity +
                              " " +
                              e.zipCode;
                        let icon = <LocationCityIcon color="primary" />;
                        if (e.isAirport) {
                          icon = <FlightTakeoffIcon htmlColor="blue" />;
                        } else if (e.isRailway) {
                          icon = <TrainIcon htmlColor="green" />;
                        }
                        return {
                          label: optionValue,
                          value: optionValue,
                          icon,
                        };
                      })}
                      type="select"
                      variant="with-search"
                      onchange={(e) => {
                        if (isDroppingAtSameLocation) {
                          setReservationDestination(e);
                        }
                        setPickupLoc(e);
                        setPickupLocation(e);
                        setStartDate(null);
                        setEndDate(null);
                      }}
                    />
                  </div>
                  {!isDroppingAtSameLocation && (
                    <div style={{ margin: "12px" }}>
                      <AppInputSwitcher
                        required={true}
                        value={reservationDestination}
                        label={translate("drop off location")}
                        key={"dropOffLocation"}
                        options={locationDropOffDetail.map((e) => {
                          const optionValue =
                            AppSettings?.showLocationWebDescription
                              ? locationLang === "it" && e.publicWebDescription
                                ? e.publicWebDescription
                                : locationLang === "en" &&
                                  e.publicWebDescriptionEN &&
                                  AppSettings?.showLocationWebDescription
                                ? e.publicWebDescriptionEN
                                : e.locationName
                              : e.locationCode +
                                "-" +
                                e.locationName +
                                " " +
                                e.locationNumber +
                                " " +
                                e.locationAddress +
                                " " +
                                e.locationCity +
                                " " +
                                e.zipCode;
                          let icon = <LocationCityIcon color="primary" />;
                          if (e.isAirport) {
                            icon = <FlightTakeoffIcon htmlColor="blue" />;
                          } else if (e.isRailway) {
                            icon = <TrainIcon htmlColor="green" />;
                          }
                          return {
                            label: optionValue,
                            value: optionValue,
                            icon,
                          };
                        })}
                        type="select"
                        variant="with-search"
                        onchange={(e) => {
                          setReservationDestination(e);
                          setEndDate(null);
                          setDropupLocation(e);
                        }}
                      />
                    </div>
                  )}
                  {AppSettings?.locationReturnEnable && (
                    <div style={{ margin: "12px" }}>
                      <div className="min-w-fit max-w-fit">
                        <AppStandardMultiSelectCheckBox
                          value={[
                            isDroppingAtSameLocation
                              ? translate("drop at same location")
                              : "",
                          ]}
                          onChange={(e) => {
                            setIsDroppingAtSameLocation(
                              !isDroppingAtSameLocation
                            );
                            if (e.length) {
                              setReservationDestination(pickupLocation);
                            }
                          }}
                          options={[translate("drop at same location")]}
                          color={
                            AppSettings?.secondaryAsBackgroundForButtons
                              ? "secondaryColor"
                              : "primary"
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ margin: "12px" }}>
                  {AppSettings?.showSeparatePickDropDate ? (
                    <>
                      {/* Start Date Picker */}
                      <DatePickerComponent
                        sx={{
                          svg: { color: "primary" },
                          input: { color: "primary" },
                          label: { color: "primary" },
                          backgroundColor: "primary",
                        }}
                        required={true}
                        disableOnDay={disableDate}
                        className="mb-4 w-full grow md:min-w-36"
                        label={pickUpDate}
                        value={reservationStartDate}
                        minDate={dayjs().add(minRentalAllowedDay, "day")}
                        maxDate={dayjs().add(365, "day")}
                        onChange={(selectedStartDate) => {
                          if (selectedStartDate) {
                            const selectedLocation = locationDetail.find(
                              (e) => {
                                const optionValue =
                                  AppSettings?.showLocationWebDescription
                                    ? locationLang === "it" &&
                                      e.publicWebDescription
                                      ? e.publicWebDescription
                                      : locationLang === "en" &&
                                        e.publicWebDescriptionEN &&
                                        AppSettings?.showLocationWebDescription
                                      ? e.publicWebDescriptionEN
                                      : e.locationName
                                    : `${e.locationCode}-${e.locationName} ${e.locationNumber} ${e.locationAddress} ${e.locationCity} ${e.zipCode}`;
                                return (
                                  optionValue?.toLowerCase() ===
                                  pickupLocation?.toLowerCase()
                                );
                              }
                            );

                            if (
                              selectedLocation &&
                              selectedLocation.onlyDropOffOutOfHours &&
                              Array.isArray(selectedLocation.closing)
                            ) {
                              const startDay = getDay(
                                selectedStartDate.toDate()
                              );

                              // Check if the selected date falls in any closed period
                              const isDateClosed =
                                selectedLocation.closing.some((entry) => {
                                  if (entry.dayOfTheWeek % 7 === startDay) {
                                    if (entry.validFrom && entry.validTo) {
                                      const validFrom = new Date(
                                        entry.validFrom
                                      );
                                      const validTo = new Date(entry.validTo);
                                      const selectedDate =
                                        selectedStartDate.toDate();

                                      return (
                                        selectedDate >= validFrom &&
                                        selectedDate <= validTo
                                      );
                                    }
                                    return true;
                                  }
                                  return false;
                                });

                              if (isDateClosed) {
                                setDateError(
                                  "The date is not available for booking."
                                );
                                setStartDate(null);
                                return;
                              }
                            }
                            // if (
                            //   selectedLocation &&
                            //   selectedLocation.onlyDropOffOutOfHours &&
                            //   Array.isArray(selectedLocation.closing)
                            // ) {
                            //   const startDay = getDay(
                            //     selectedStartDate.toDate()
                            //   );
                            //   const isClosed = selectedLocation.closing.some(
                            //     (entry) => entry.dayOfTheWeek % 7 === startDay
                            //   );
                            //   const isOpened = selectedLocation.openings.some(
                            //     (entry) => entry.dayOfTheWeek % 7 === startDay
                            //   );
                            //
                            //   if (isClosed || !isOpened) {
                            //     setDateError(
                            //       "The Start date is not available for booking."
                            //     );
                            //     setStartDate(null);
                            //     return;
                            //   } else {
                            //     setDateError("");
                            //   }
                            // }

                            const opening = selectedLocation?.openings?.find(
                              (e) =>
                                getDay(selectedStartDate.toDate()) ===
                                e.dayOfTheWeek % 7
                            )?.startTime;

                            setStartDate(
                              setTime(opening || "", selectedStartDate.toDate())
                            );
                          } else {
                            setStartDate(null);
                          }
                        }}
                      />
                      {/* End Date Picker */}
                      <DatePickerComponent
                        sx={{
                          svg: { color: "primary" },
                          input: { color: "primary" },
                          label: { color: "primary" },
                          backgroundColor: "primary",
                        }}
                        className="w-full grow md:min-w-36"
                        required={true}
                        disableOnDay={disableDate}
                        label={dropOffDate}
                        value={reservationEndDate}
                        minDate={
                          reservationStartDate
                            ? dayjs(reservationStartDate)
                            : dayjs().add(minRentalAllowedDay, "day")
                        }
                        maxDate={
                          reservationStartDate
                            ? dayjs(reservationStartDate).add(
                                maxRentalAllowedDay,
                                "day"
                              )
                            : dayjs().add(365, "day")
                        }
                        onChange={(selectedEndDate) => {
                          if (selectedEndDate) {
                            const selectedLocation = locationDetail.find(
                              (e) => {
                                const optionValue =
                                  AppSettings?.showLocationWebDescription
                                    ? locationLang === "it" &&
                                      e.publicWebDescription
                                      ? e.publicWebDescription
                                      : locationLang === "en" &&
                                        e.publicWebDescriptionEN &&
                                        AppSettings?.showLocationWebDescription
                                      ? e.publicWebDescriptionEN
                                      : e.locationName
                                    : `${e.locationCode}-${e.locationName} ${e.locationNumber} ${e.locationAddress} ${e.locationCity} ${e.zipCode}`;
                                return (
                                  optionValue?.toLowerCase() ===
                                  reservationDestination?.toLowerCase()
                                );
                              }
                            );

                            const opening = selectedLocation?.openings?.find(
                              (e) =>
                                getDay(selectedEndDate.toDate()) ===
                                e.dayOfTheWeek % 7
                            )?.startTime;

                            setEndDate(
                              setTime(opening || "", selectedEndDate.toDate())
                            );
                          } else {
                            setEndDate(null);
                          }
                        }}
                      />
                    </>
                  ) : (
                    <AppInputSwitcher
                      disableOnDay={disableDate}
                      required={true}
                      className="w-full grow md:min-w-72"
                      label={translate("reservation dates")}
                      key={"reservationDates"}
                      options={[]}
                      startDate={reservationStartDate}
                      endDate={reservationEndDate}
                      minDate={addDays(new Date(), minRentalAllowedDay)}
                      maxDate={
                        reservationStartDate
                          ? addDays(
                              new Date(reservationStartDate?.toString()),
                              maxRentalAllowedDay
                            )
                          : addDays(new Date(), 365)
                      }
                      type="date"
                      variant="range"
                      onchange={(selectedDateRange) => {
                        if (selectedDateRange[0]) {
                          const selectedLocation = locationDetail.filter(
                            (e) => {
                              const optionValue =
                                AppSettings?.showLocationWebDescription
                                  ? locationLang === "it" &&
                                    e.publicWebDescription
                                    ? e.publicWebDescription
                                    : locationLang === "en" &&
                                      e.publicWebDescriptionEN &&
                                      AppSettings?.showLocationWebDescription
                                    ? e.publicWebDescriptionEN
                                    : e.locationName
                                  : e.locationCode +
                                    "-" +
                                    e.locationName +
                                    " " +
                                    e.locationNumber +
                                    " " +
                                    e.locationAddress +
                                    " " +
                                    e.locationCity +
                                    " " +
                                    e.zipCode;
                              return (
                                optionValue?.toLowerCase() ===
                                pickupLocation?.toLowerCase()
                              );
                            }
                          )[0];

                          if (
                            selectedLocation &&
                            selectedLocation.onlyDropOffOutOfHours &&
                            Array.isArray(selectedLocation.closing)
                          ) {
                            const startDay = getDay(selectedDateRange[0]);

                            // Check if the selected date falls in any closed period
                            const isDateClosed = selectedLocation.closing.some(
                              (entry) => {
                                if (entry.dayOfTheWeek % 7 === startDay) {
                                  if (entry.validFrom && entry.validTo) {
                                    const validFrom = new Date(entry.validFrom);
                                    const validTo = new Date(entry.validTo);
                                    const selectedDate = selectedDateRange[0];

                                    return (
                                      selectedDate >= validFrom &&
                                      selectedDate <= validTo
                                    );
                                  }
                                  return true;
                                }
                                return false;
                              }
                            );

                            if (isDateClosed) {
                              setDateError(
                                "The date is not available for booking."
                              );
                              setStartDate(null);
                              return;
                            }
                          }

                          // if (
                          //   selectedLocation &&
                          //   selectedLocation.onlyDropOffOutOfHours &&
                          //   Array.isArray(selectedLocation.closing)
                          // ) {
                          //   const startDay = getDay(selectedDateRange[0]);
                          //   const isClosed = selectedLocation.closing.some(
                          //     (entry) => entry.dayOfTheWeek % 7 === startDay
                          //   );
                          //   const isOpened = selectedLocation.openings.some(
                          //     (entry) => entry.dayOfTheWeek % 7 === startDay
                          //   );
                          //   if (isClosed || !isOpened) {
                          //     setDateError(
                          //       "The date is not available for booking."
                          //     );
                          //     setStartDate(null);
                          //     return;
                          //   } else {
                          //     setDateError("");
                          //   }
                          // }

                          if (selectedLocation) {
                            const opening = selectedLocation.openings.filter(
                              (e) =>
                                getDay(selectedDateRange[0]) ===
                                e.dayOfTheWeek % 7
                            )[0];

                            const startTime = opening
                              ? opening.LocationStartTime || opening.startTime
                              : "";

                            setStartDate(
                              setTime(startTime, selectedDateRange[0])
                            );
                          }
                        } else {
                          setStartDate(null);
                        }
                        if (selectedDateRange[1]) {
                          const selectedLocation = locationDetail.filter(
                            (e) => {
                              const optionValue =
                                AppSettings?.showLocationWebDescription
                                  ? locationLang === "it" &&
                                    e.publicWebDescription
                                    ? e.publicWebDescription
                                    : locationLang === "en" &&
                                      e.publicWebDescriptionEN &&
                                      AppSettings?.showLocationWebDescription
                                    ? e.publicWebDescriptionEN
                                    : e.locationName
                                  : e.locationCode +
                                    "-" +
                                    e.locationName +
                                    " " +
                                    e.locationNumber +
                                    " " +
                                    e.locationAddress +
                                    " " +
                                    e.locationCity +
                                    " " +
                                    e.zipCode;
                              return (
                                optionValue?.toLowerCase() ===
                                reservationDestination.toLowerCase()
                              );
                            }
                          )[0];

                          if (selectedLocation) {
                            const opening = selectedLocation.openings.filter(
                              (e) =>
                                getDay(selectedDateRange[1]) ===
                                e.dayOfTheWeek % 7
                            )[0];

                            const endTime = opening
                              ? opening.LocationStartTime || opening.startTime
                              : "";

                            setEndDate(setTime(endTime, selectedDateRange[1]));
                          }
                        } else {
                          setEndDate(null);
                        }
                      }}
                    />
                  )}
                </div>
                {dateError && (
                  <div className="text-red-500 text-sm mt-1">{dateError}</div>
                )}
                <div
                  style={{
                    margin: "12px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <AppInputSwitcher
                    required={true}
                    disabled={!reservationStartDate}
                    className="!w-[49%] min-w-[5rem]"
                    label={pickUpTime}
                    key={"pickUpTime"}
                    enableTimesInRange={getEnabledTimeSlots(
                      locationDetail,
                      pickupLocation,
                      reservationStartDate
                    )}
                    value={reservationStartDate}
                    options={[]}
                    type="time"
                    variant="slot"
                    onchange={(e) => {
                      setStartDate(e);
                    }}
                  />
                  <AppInputSwitcher
                    enableTimesInRange={getEnabledDropoffTimeSlots(
                      locationDetail,
                      reservationDestination,
                      reservationEndDate
                    )}
                    disabled={!reservationEndDate}
                    required={true}
                    className="!w-[49%] min-w-[5rem]"
                    label={dropOffTime}
                    key={"dropOffTime"}
                    options={[]}
                    value={reservationEndDate}
                    type="time"
                    variant="slot"
                    onchange={(e) => {
                      setEndDate(e);
                    }}
                  />
                </div>
                {AppSettings?.showMacroGroupSelection && (
                  <AppInputSwitcher
                    required={false}
                    className={"w-full"}
                    type="select"
                    variant="with-search"
                    label={translate("Macro Group")}
                    options={macroGroupOptions?.map((e) => e.label) || []}
                    value={selectedMacroGroup}
                    onchange={(e) => {
                      const selectedOption = macroGroupOptions?.find(
                        (option) => option.label === e
                      );
                      if (selectedOption) {
                        setSelectedMacroGroup(selectedOption.label);
                        setSelectedMacroGroupWebCode(selectedOption.WebCode);
                      }
                    }}
                  />
                )}
                {disableCoupon === false &&
                  (mountedRef.current &&
                  reservationSourceEnabled === true &&
                  reservationSourceForUser?.length > 0 ? (
                    <div className="flex" style={{ margin: "12px" }}>
                      <AppInputSwitcher
                        required={true}
                        className="w-full min-w-[12rem]"
                        label={
                          userId
                            ? bookingResSourceForReservedArea
                            : bookingReservationSource
                        }
                        key={"reservationPlatform"}
                        options={reservationSourceForUser?.map(
                          (e) =>
                            e["description"] ||
                            e["Ragione Sociale textRS"] ||
                            e["Ragione Sociale"]
                        )}
                        type="select"
                        variant="with-search"
                        onchange={(e) => {
                          setReservationPlatform(e);
                          const selectedObj = reservationSourceForUser.find(
                            (item) =>
                              item["description"] === e ||
                              item["Ragione Sociale textRS"] === e ||
                              item["Ragione Sociale"]
                          );
                          setReservedAreaPayNow(selectedObj?.payNow ?? false);
                        }}
                        value={reservationPlatform}
                      />
                    </div>
                  ) : (
                    AppSettings?.showReservationGroupSelection &&
                    !isScriptChannelResSource &&
                    !reservationSourceEnabled && (
                      <div className="flex" style={{ margin: "12px" }}>
                        <AppInputSwitcher
                          required={true}
                          className="w-full min-w-[12rem]"
                          label={bookingReservationSource}
                          key={"reservationPlatform"}
                          options={reservationSourceOptions?.map(
                            (e) =>
                              e["Ragione Sociale textRS"] ||
                              e["Ragione Sociale"]
                          )}
                          value={reservationPlatform}
                          type="select"
                          variant="with-search"
                          onchange={(e) => {
                            setReservationPlatform(e);
                            const selectedObj = reservationSourceOptions.find(
                              (item) =>
                                item["Ragione Sociale"] === e ||
                                item["Ragione Sociale textRS"] === e
                            );
                            setReservedAreaPayNow(selectedObj?.payNow ?? false);
                          }}
                        />
                      </div>
                    )
                  ))}{" "}
                {AppSettings?.showWebCodeInput && (
                  <div style={{ margin: "12px" }}>
                    <AppInputSwitcher
                      required={false}
                      autoComplete="off"
                      className="!w-[49%] min-w-[5rem]"
                      label={codiceLabel}
                      key={"Voucher Code"}
                      placeholder={codiceLabel}
                      options={[]}
                      value={agreementCoupon}
                      type="text"
                      variant="slot"
                      disabledCoupon={disableCoupon}
                      onchange={(e) => {
                        setagreementCoupon(e);
                      }}
                    />
                  </div>
                )}
                {showModal && validateAgreementCoupon && (
                  <div
                    className="modal-overlay"
                    style={{
                      position: "fixed",
                      top: "0",
                      left: "0",
                      right: "0",
                      bottom: "0",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: "1001",
                    }}
                  >
                    <div
                      className="modal-content"
                      style={{
                        background: "white",
                        width: "300px",
                        padding: "15px",
                        borderRadius: "8px",
                        textAlign: "center",
                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                        zIndex: "1000",
                      }}
                    >
                      <p>{modalMessage}</p>
                      <button
                        onClick={() => setShowModal(false)}
                        className="close-button"
                        style={{
                          backgroundColor:
                            dynamicTheme?.palette["primary"]?.main,
                          color: "white",
                          border: "none",
                          padding: "10px 20px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          marginTop: "10px",
                        }}
                      >
                        {translate("Close")}
                        {/*Close*/}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="gap-2 w-full ml-[30px]">
                <div>
                  {Object.entries(ageCriteriaData)
                    .map(([key, value]) => {
                      switch (key.toUpperCase()) {
                        case "YOUNGDRIVER":
                        case "YOUNGDRIVER2":
                        case "NOAGEDRIVER":
                        case "SENIORDRIVER":
                          return (
                            <div key={key} style={{ marginBottom: "6px" }}>
                              <AppRadioButton
                                required={true}
                                color={
                                  AppSettings?.secondaryAsBackgroundForButtons
                                    ? "secondaryColor"
                                    : "primary"
                                }
                                value={isAgeLimitSelected}
                                className="w-full text-xs"
                                onChange={(e) => {
                                  setIsAgeLimitSelected(e);
                                }}
                                options={[
                                  {
                                    label: t(value),
                                    value: key,
                                  },
                                ]}
                              />
                            </div>
                          );
                        default:
                          return null;
                      }
                    })
                    .filter((option) => option !== null)}
                </div>
                <div style={{ marginRight: "20px" }}>
                  <AppButton
                    type={"submit"}
                    endIcon={<SearchIcon />}
                    className="w-[100%] h-14 w-full"
                    color={
                      AppSettings?.secondaryAsBackgroundForButtons
                        ? "secondaryColor"
                        : "primary"
                    }
                    style={{
                      borderRadius: `${AppSettings?.buttonBorderRadius || 0}px`,
                    }}
                  >
                    <p className="w-full text-center">{searchText}</p>
                  </AppButton>
                </div>
                {AppSettings?.showTextBeginningOfBookingForm && (
                  <div className="w-full">
                    <p
                      className="self-start"
                      style={{
                        color: dynamicTheme?.palette["secondaryColor"]?.main,
                      }}
                    >
                      {showTextBeginningOfBookingFormMsg}
                    </p>
                  </div>
                )}
              </div>
            </AppCard>
          )}
        </form>
      </main>
    </ThemeProvider>
  );
}

export default BookingForm;
